// Events, Supplies, Employees, Documents, Reports, Settings pages
// Each follows the same pattern as Leaves and Hours
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fmtLeaveDays, leaveLeft, Hint, Badge, PersonCell, Modal, fullName, useDateFilter, DateFilter, useToast, RoleBadge, Toggle, EmployeeFilter, uniqueUsers, useSort, SortTh, ROLE_LABELS, LEAVE_LABELS, isDirty } from '../components/UI';
import api from '../utils/api';
import { SettlementSpec, PersonEventsModal } from '../components/PaySpec';

// ========== SUPPLIES ==========
const SUPPLY_SORT = {
  employee: s => s.user ? `${s.user.lastName} ${s.user.firstName}` : '',
  createdAt: s => String(s.createdAt),
  name: s => s.name,
  priority: s => s.priority === 'URGENT' ? 1 : 0,
  status: s => s.status,
};
const SUPPLY_STATUS_OPTIONS = [['PENDING', 'Zgłoszone'], ['APPROVED', 'Zatwierdzone'], ['ORDERED', 'Zamówione'], ['DELIVERED', 'Dostarczone'], ['REJECTED', 'Odrzucone']];

/** 12.10: kolory priorytetów zapotrzebowania — pilne na czerwono (i podświetlony wiersz, dopóki nie zamówione), normalne neutralnie. */
const PRIORITY_STYLE = {
  URGENT: { background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5', label: '🔴 Pilne' },
  NORMAL: { background: '#E0F2FE', color: '#0369A1', border: '1px solid #BAE6FD', label: 'Normalny' },
};
const PriorityBadge = ({ p }) => {
  const st = PRIORITY_STYLE[p] || PRIORITY_STYLE.NORMAL;
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: st.background, color: st.color, border: st.border, whiteSpace: 'nowrap' }}>{st.label}</span>;
};

// T139: przy linku pokazujemy nazwę sklepu (np. allegro.pl), nie samo „Link” — podstawiony adres od razu widać
const linkHost = (l) => { try { return new URL(l).hostname.replace(/^www\./, ''); } catch { return 'link'; } };
const EMPTY_SUPPLY = { name: '', quantity: '', priority: 'NORMAL', link: '', description: '' };
const dPL = (d) => new Date(d).toLocaleDateString('pl-PL');
const shortName = (p) => p ? `${p.firstName} ${(p.lastName || '').slice(0, 1)}.` : '';

/** T110: postęp zgłoszenia — kto zatwierdził / odrzucił (z powodem), zamówienie z terminem, dostawa. */
function SupplyProgress({ s }) {
  const parts = [];
  if (s.status === 'REJECTED') {
    parts.push(<span key="r" style={{ color: '#B91C1C' }}>✗ odrzucił(a) {shortName(s.approvedBy)}{s.approvedAt ? ` ${dPL(s.approvedAt)}` : ''}{s.rejectReason ? <>: <i>„{s.rejectReason}”</i></> : ''}</span>);
  } else if (s.approvedAt && s.status !== 'PENDING') {
    parts.push(<span key="a" style={{ color: '#059669' }}>✓ zatwierdził(a) {shortName(s.approvedBy)} {dPL(s.approvedAt)}</span>);
  }
  if (s.orderedAt && ['ORDERED', 'DELIVERED'].includes(s.status)) {
    parts.push(<span key="o" style={{ color: '#7C3AED' }}>🛒 zamówione {dPL(s.orderedAt)}{s.orderedBy ? ` (${shortName(s.orderedBy)})` : ''}
      {s.status === 'ORDERED' && s.expectedAt ? <b> · będzie ok. {dPL(s.expectedAt)}</b> : ''}{s.orderNote ? <> · <i>{s.orderNote}</i></> : ''}</span>);
  }
  if (s.status === 'DELIVERED' && s.deliveredAt) parts.push(<span key="d" style={{ color: '#0369A1' }}>📦 dostarczone {dPL(s.deliveredAt)}</span>);
  if (!parts.length) return null;
  return <div style={{ fontSize: 10, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>{parts}</div>;
}

export function SuppliesPage() {
  const { user, isWorker, isAdmin } = useAuth();
  const [supplies, setSupplies] = useState([]);
  const [form, setForm] = useState(null);          // { ...pola, id? } — nowe albo poprawiane zgłoszenie
  const [initialForm, setInitialForm] = useState(EMPTY_SUPPLY);
  const [reject, setReject] = useState(null);      // { s, reason }
  const [order, setOrder] = useState(null);        // { s, note, expectedAt }
  // T110: filtr z linku paska „Czeka na Ciebie” (?status=PENDING / APPROVED)
  const [filter, setFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState('');
  const df = useDateFilter();
  const { show, ToastComponent } = useToast();

  const people = uniqueUsers(supplies.map(s => s.user));
  const visible = employee ? supplies.filter(s => s.userId === employee) : supplies;
  const ss = useSort(visible, SUPPLY_SORT, 'createdAt');

  const load = useCallback(async () => {
    try { const p = new URLSearchParams(); if (filter) p.set('status', filter); if (df.from) p.set('from', df.from); if (df.to) p.set('to', df.to); setSupplies(await api.getSupplies(p.toString())); } catch (e) { show(e.message, 'error'); }
    setLoading(false);
  }, [filter, df.from, df.to]);
  useEffect(() => { load(); }, [load]);

  // T109: każda akcja z komunikatem błędu (wcześniej odmowa serwera ginęła — „przycisk nic nie robi”)
  const act = (p, ok, after) => p.then(() => { if (ok) show(ok); if (after) after(); load(); }).catch(err => show(err.message, 'error'));
  const anyAction = isAdmin || supplies.some(s => s.actions && Object.values(s.actions).some(Boolean));

  const openNew = () => { setInitialForm(EMPTY_SUPPLY); setForm(EMPTY_SUPPLY); };
  const openEdit = (s) => { const f = { name: s.name, quantity: s.quantity || '', priority: s.priority, link: s.link || '', description: s.description || '' }; setInitialForm(f); setForm({ ...f, id: s.id }); };
  const submit = () => {
    const { id, ...data } = form;
    act(id ? api.updateSupply(id, data) : api.createSupply(data), id ? 'Zgłoszenie poprawione' : 'Zgłoszenie wysłane!', () => setForm(null));
  };
  const withdraw = (s) => { if (window.confirm(`Wycofać zgłoszenie „${s.name}”?`)) act(api.withdrawSupply(s.id), 'Zgłoszenie wycofane'); };
  const safeLink = (l) => /^https?:\/\//i.test(l || '');
  const formFields = form ? (({ id, ...f }) => f)(form) : null;

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Zapotrzebowanie</h2>
        {user?.role !== 'EVENT' && <button className="btn btn-primary" onClick={openNew}>+ Nowe zgłoszenie</button>}
      </div>
      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo} onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick}>
        {!isWorker && <EmployeeFilter people={people} value={employee} onChange={setEmployee} />}
      </DateFilter>
      <div className="card">
        <div className="filter-pills" style={{ marginBottom: 8 }}>
          {[['', 'Wszystkie'], ['PENDING', 'Oczekujące'], ['APPROVED', 'Do zamówienia'], ['ORDERED', 'Zamówione'], ['DELIVERED', 'Dostarczone'], ['REJECTED', 'Odrzucone']].map(([v, l]) => (
            <button key={v} className={`filter-pill ${filter === v ? 'active' : ''}`} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 10 }}>
          Zgłoszone → zatwierdza przełożony → zamawia osoba od zamówień → dostarczone. Swoje zgłoszenie możesz poprawić albo wycofać, dopóki czeka.
        </div>
        {loading ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <SortTh label="Zgłaszający" k="employee" sort={ss.sort} onSort={ss.toggle} />
                <SortTh label="Data" k="createdAt" sort={ss.sort} onSort={ss.toggle} />
                <SortTh label="Co i postęp" k="name" sort={ss.sort} onSort={ss.toggle} />
                <th>Ilość</th>
                <SortTh label="Priorytet" k="priority" sort={ss.sort} onSort={ss.toggle} />
                <th>Link</th>
                <SortTh label="Status" k="status" sort={ss.sort} onSort={ss.toggle} />
                {anyAction && <th>Akcja</th>}
              </tr></thead>
              <tbody>
                {ss.sorted.map(s => {
                  const a = s.actions || {};
                  const own = s.userId === user?.id;
                  const none = !Object.values(a).some(Boolean);
                  return (
                    <tr key={s.id} style={s.priority === 'URGENT' && ['PENDING', 'APPROVED'].includes(s.status) ? { background: '#FEF2F2' } : undefined}>
                      <td><PersonCell name={fullName(s.user)} /></td>
                      <td>{dPL(s.createdAt)}</td>
                      <td style={{ maxWidth: 340 }}>
                        <b>{s.name}</b>
                        {s.description && <div style={{ fontSize: 10, color: '#64748B', whiteSpace: 'pre-wrap' }}>{s.description}</div>}
                        <SupplyProgress s={s} />
                      </td>
                      <td>{s.quantity}</td>
                      <td><PriorityBadge p={s.priority} /></td>
                      <td>{safeLink(s.link) ? <a href={s.link} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer" style={{ color: '#2563EB', fontSize: 10, wordBreak: 'break-all' }}
                        title={`${s.link}\n\nSprawdź adres sklepu przed zakupem. Nie loguj się przez ten link — wejdź do sklepu sam.`}>🔗 {linkHost(s.link)}</a> : '—'}</td>
                      <td><Badge status={s.status} /></td>
                      {anyAction && (
                        <td>
                          <div className="flex gap-4 flex-wrap" style={{ alignItems: 'center' }}>
                            {a.decide && <button className="btn btn-success btn-sm" title="Zatwierdź" onClick={() => act(api.approveSupply(s.id), 'Zatwierdzone')}>✓</button>}
                            {a.order && s.status === 'APPROVED' && <button className="btn btn-primary btn-sm" onClick={() => setOrder({ s, note: '', expectedAt: '' })}>🛒 Zamówione</button>}
                            {a.order && s.status === 'ORDERED' && <button className="btn btn-ghost btn-sm" title="Zmień termin albo uwagę" onClick={() => setOrder({ s, note: s.orderNote || '', expectedAt: s.expectedAt ? String(s.expectedAt).slice(0, 10) : '' })}>✎ termin</button>}
                            {a.deliver && <button className="btn btn-outline btn-sm" onClick={() => act(api.deliverSupply(s.id), 'Dostarczone')}>{own && !a.order ? '📦 Odebrałem' : '📦 Dostarczono'}</button>}
                            {a.reject && <button className="btn btn-danger btn-sm" title={s.status === 'APPROVED' ? 'Nie zamawiamy (z powodem)' : 'Odrzuć (z powodem)'} onClick={() => setReject({ s, reason: '' })}>✗</button>}
                            {a.edit && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}>✎ Popraw</button>}
                            {a.edit && <button className="btn btn-ghost btn-sm" style={{ color: '#B91C1C' }} onClick={() => withdraw(s)}>Wycofaj</button>}
                            {none && !isAdmin && <span style={{ fontSize: 10, color: '#94A3B8' }} title={own ? 'Własnego zgłoszenia nie rozpatrujesz' : 'Rozpatruje przełożony zgłaszającego'}>—</span>}
                            {isAdmin && (
                              // Admin może ustawić dowolny status, także cofnąć (T34)
                              <select value={s.status} title="Admin: ręczna zmiana statusu" style={{ fontSize: 10, padding: '2px 4px' }}
                                onChange={e => act(api.updateSupplyStatus(s.id, e.target.value), 'Status zmieniony')}>
                                {SUPPLY_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {ss.sorted.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak zgłoszeń</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {form && (
        <Modal title={form.id ? 'Popraw zgłoszenie' : 'Nowe zgłoszenie'} onClose={() => setForm(null)} dirty={isDirty(formFields, initialForm)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="input-group"><label>Co potrzebne?</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Taśma pakowa 48mm" /></div>
            <div className="flex gap-8"><div className="input-group flex-1"><label>Ilość</label><input value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="10 szt." /></div>
            <div className="input-group flex-1"><label>Priorytet</label><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}><option value="NORMAL">Normalny</option><option value="URGENT">Pilne</option></select></div></div>
            <div className="input-group"><label>Link do produktu</label><input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="https://allegro.pl/..." /></div>
            <div className="input-group"><label>Opis</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Do czego, dlaczego..." /></div>
            <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={() => setForm(null)}>Anuluj</button><button className="btn btn-primary" onClick={submit}>{form.id ? 'Zapisz' : 'Wyślij'}</button></div>
          </div>
        </Modal>
      )}
      {reject && (
        <Modal title={reject.s.status === 'APPROVED' ? 'Nie zamawiamy' : 'Odrzuć zgłoszenie'} onClose={() => setReject(null)} dirty={!!reject.reason.trim()}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12 }}><b>{reject.s.name}</b> — {fullName(reject.s.user)}</div>
            <div className="input-group"><label>Powód (zobaczy go zgłaszający)</label>
              <textarea value={reject.reason} onChange={e => setReject({ ...reject, reason: e.target.value })} placeholder="np. mamy jeszcze zapas w magazynie" autoFocus /></div>
            <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={() => setReject(null)}>Anuluj</button>
              <button className="btn btn-danger" disabled={reject.reason.trim().length < 3} onClick={() => act(api.rejectSupply(reject.s.id, reject.reason), 'Odrzucone', () => setReject(null))}>Odrzuć</button></div>
          </div>
        </Modal>
      )}
      {order && (
        <Modal title={order.s.status === 'ORDERED' ? 'Termin dostawy' : 'Oznacz jako zamówione'} onClose={() => setOrder(null)}
          dirty={order.note !== (order.s.orderNote || '') || order.expectedAt !== (order.s.expectedAt ? String(order.s.expectedAt).slice(0, 10) : '')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12 }}><b>{order.s.name}</b> {order.s.quantity && `(${order.s.quantity})`} — {fullName(order.s.user)}</div>
            <div className="input-group" style={{ maxWidth: 220 }}><label>Kiedy będzie? (opcjonalnie)</label>
              <input type="date" value={order.expectedAt} onChange={e => setOrder({ ...order, expectedAt: e.target.value })} /></div>
            <div className="input-group"><label>Uwaga dla zgłaszającego (opcjonalnie)</label>
              <input value={order.note} onChange={e => setOrder({ ...order, note: e.target.value })} placeholder="np. kurier w czwartek, zamiennik innej firmy" /></div>
            <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={() => setOrder(null)}>Anuluj</button>
              <button className="btn btn-primary" onClick={() => act(api.orderSupply(order.s.id, { note: order.note, expectedAt: order.expectedAt || null }), order.s.status === 'ORDERED' ? 'Zapisane' : 'Oznaczone jako zamówione', () => setOrder(null))}>Zapisz</button></div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}

// ========== EMPLOYEES ==========
// T132: krótkie wyjaśnienia uprawnień — podpowiedź po najechaniu myszką na nazwę przełącznika
const PERM_HINTS = {
  canApproveHours: 'Zatwierdza, odrzuca i poprawia godziny magazynowe i nocne oraz zwykłe dodatki swoich podwładnych, a także ich zapotrzebowanie. Godzin eventowych nie widzi — te zatwierdza opiekun eventowych.',
  canApproveLeaves: 'Zatwierdza urlopy i pracę zdalną swoich podwładnych, może wycofać ich zatwierdzony urlop. Widzi kalendarz nieobecności swojego zespołu i ostrzeżenie, gdy ktoś z zespołu ma już wolne.',
  canSettleLeaves: 'Widzi zatwierdzone urlopy całej firmy i salda. Po wprowadzeniu wniosku do Enovy klika „Zaakceptowany, wprowadź do Enovy”. Zwykle księgowa — handlowcom wyłącz.',
  canSettlePayroll: 'Ekran „Rozliczenia”: zamyka okresy do wypłaty wszystkich, wybiera przelew albo gotówkę, pobiera pliki. Widzi zarobki wszystkich.',
  canAccounting: 'Ekran „Kadry”: stawki wszystkich (tylko podgląd), przełożeni, pula urlopu do ustawienia. Tylko ta osoba i admin widzą liczbę dni pracy zdalnej i jej ewidencję.',
  canOrderSupplies: 'Widzi wszystkie zatwierdzone zgłoszenia zapotrzebowania i — jako jedyna obok admina — oznacza je jako zamówione, dostarczone albo „nie zamawiamy”.',
  canManageEvents: 'Tworzy i edytuje eventy, zmienia ich status, układa skład. Widzi wszystkie eventy i dostępność pracowników eventowych.',
  canAssignEvents: 'Dodaje i zdejmuje ludzi w istniejących eventach (bez tworzenia eventów). Widzi odwołania w składach.',
  canApproveEventHours: 'Opiekun eventowych: zatwierdza godziny eventowe, dniówki i wydatki pod eventami u każdego pracownika, weryfikuje Kamilkową i niekaralność. Godziny eventowe zatwierdza tylko on i admin.',
  hasLeaveEntitlement: 'Może składać wnioski urlopowe — dni schodzą z puli poniżej. Pracownikom eventowym (zlecenie) urlop nie przysługuje.',
  leaveDaysTotal: 'Ile dni urlopu przysługuje w roku: zwykle 20 (staż poniżej 10 lat) albo 26.',
  leaveDaysUsed: 'Dni już wykorzystane w tym roku. Zatwierdzone urlopy dopisują się same — zmieniaj tylko przy korekcie.',
  deputyId: 'Gdy ta osoba jest nieobecna, zastępca widzi i zatwierdza wnioski jej podwładnych — o ile sam ma uprawnienia do zatwierdzania.',
  warehouseWorkEnabled: 'Może wpisywać godziny Magazyn i Nocna; pokazują się stawki magazynowe. Pracownik fizyczny ma to zawsze.',
  animationWorkEnabled: 'Może dostać na evencie funkcję „Animator(ka)”. Wtedy czas trwania eventu (np. impreza 15–19 = 4 h) liczy się stawką animacji, a reszta wpisu (dojazd, powrót) stawką eventową. Godziny zatwierdza opiekun eventowych.',
};

// Opcje rol w formularzach - etykiety z ROLE_LABELS (jedno zrodlo), z dopiskiem co robi rola
const ROLE_OPTIONS = [
  ['WAREHOUSE', 'Pracownik fizyczny'], ['EVENT', 'Pracownik eventowy'],
  ['COORDINATOR', ROLE_LABELS.COORDINATOR], ['ADMINISTRATION', `${ROLE_LABELS.ADMINISTRATION} (rozlicza wnioski)`],
  ['ADMIN', `${ROLE_LABELS.ADMIN} (pełne uprawnienia)`],
];
const LICENSE_CATEGORIES = ['AM', 'A1', 'A2', 'A', 'B', 'B+E', 'C', 'C+E', 'D', 'T'];
// W bazie prawo jazdy to tekst "B, C+E"; w formularzu — lista zaznaczonych kategorii
const licenseToList = (s) => String(s || '').split(',').map(x => x.trim()).filter(Boolean);

// Pola wyboru kategorii prawa jazdy (T11)
function LicensePicker({ value, onChange }) {
  const selected = Array.isArray(value) ? value : licenseToList(value);
  const toggle = (cat) => onChange(selected.includes(cat) ? selected.filter(c => c !== cat) : [...selected, cat]);
  return (
    <div className="input-group">
      <label>Prawo jazdy {selected.length ? <span style={{ color: '#2563EB' }}>— {LICENSE_CATEGORIES.filter(c => selected.includes(c)).join(', ')}</span> : <span style={{ color: '#94A3B8' }}>— brak</span>}</label>
      <div className="flex gap-4 flex-wrap">
        {LICENSE_CATEGORIES.map(cat => (
          <button key={cat} type="button" className={`filter-pill ${selected.includes(cat) ? 'active' : ''}`} onClick={() => toggle(cat)}>{cat}</button>
        ))}
      </div>
    </div>
  );
}

/**
 * T76: rodzaj pracy → stawki. Rola Magazyn: magazyn zawsze; Eventowy: eventy zawsze i nigdy magazyn;
 * Biuro / koordynator: wg przełączników. Wyłączenie przełącznika ukrywa stawki, ale ich nie kasuje.
 */
function WorkKinds({ data, set }) {
  const role = data.role;
  const wh = role === 'WAREHOUSE' || !!data.warehouseWorkEnabled;
  const ev = role === 'EVENT' || !!data.eventWorkEnabled;
  const fixed = { fontSize: 11, color: '#475569', background: '#F1F5F9', borderRadius: 6, padding: '6px 8px' };
  const num = (k, label) => (
    <div className="input-group flex-1" style={{ minWidth: 110 }}><label>{label}</label>
      <input type="number" min="0" step="0.01" value={data[k] ?? ''} onChange={e => set(k, e.target.value)} placeholder="domyślna" /></div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {role === 'WAREHOUSE'
        ? <div style={fixed}>🏭 <b>Praca fizyczna</b> (magazyn, warsztat) — główna praca tej osoby (zawsze włączona, etat)</div>
        : <Toggle on={!!data.warehouseWorkEnabled} onChange={v => set('warehouseWorkEnabled', v)} label="Praca na magazynie (np. pomaga przy rozładunku kontenera, nocki)" hint={PERM_HINTS.warehouseWorkEnabled} />}
      {wh && <div className="flex gap-8 flex-wrap">{num('rateWarehouse', role === 'WAREHOUSE' ? 'Nadgodziny — magazyn (zł/h)' : 'Magazyn (zł/h)')}{num('rateNight', role === 'WAREHOUSE' ? 'Godziny nocne (zł/h)' : 'Nocna (zł/h)')}</div>}
      {role === 'WAREHOUSE' && <div style={{ fontSize: 10, color: '#64748B' }}>Etat: pensję liczy księgowość poza aplikacją. Aplikacja płaci godziny dzienne ponad normę miesiąca (dni robocze × 8 h, minus urlop/L4) stawką „magazyn” i wszystkie godziny nocne stawką „nocne” — bez automatycznych dodatków.</div>}
      {role === 'EVENT'
        ? <div style={fixed}>🎪 <b>Praca na eventach</b> — główna praca tej osoby (zawsze włączona; rozliczana godzinowo, bez urlopu)</div>
        : <Toggle on={!!data.eventWorkEnabled} onChange={v => set('eventWorkEnabled', v)} label="Praca na eventach" />}
      {ev && <div className="flex gap-8 flex-wrap">{num('rateEvent', 'Event (zł/h)')}{num('rateEventDaily', 'Dniówka (zł)')}</div>}
      {ev && <Toggle on={!!data.animationWorkEnabled} onChange={v => set('animationWorkEnabled', v)} label="Praca jako animator(ka)" hint={PERM_HINTS.animationWorkEnabled} />}
      {ev && data.animationWorkEnabled && <div className="flex gap-8 flex-wrap">{num('rateAnimation', 'Animacja (zł/h)')}{num('rateAnimationDaily', 'Animacja — dniówka (zł)')}</div>}
      {!wh && !ev && <div style={{ fontSize: 10, color: '#94A3B8' }}>Bez rodzaju pracy — ta osoba nie wpisuje godzin, więc stawki są ukryte.</div>}
      <div style={{ fontSize: 10, color: '#94A3B8' }}>Puste pole = stawka domyślna z Ustawień. Wyłączenie przełącznika ukrywa stawki, ale ich nie kasuje.</div>
    </div>
  );
}

/** T78: podpowiedzi stanowisk z już używanych — żeby nie powstawały warianty tej samej nazwy. */
function PositionsList({ users = [] }) {
  const list = [...new Set(['Opiekun pracowników eventowych', 'Magazynier', 'Spawacz', 'Tapicer', ...users.map(u => (u.position || '').trim()).filter(Boolean)])].sort((a, b) => a.localeCompare(b, 'pl'));
  return <datalist id="positions-list">{list.map(p => <option key={p} value={p} />)}</datalist>;
}

// Wybór przełożonego — tylko osoby, które mogą zatwierdzać (lub admin), bez samego siebie
function SupervisorSelect({ value, onChange, allUsers, selfId }) {
  const candidates = allUsers.filter(x => x.id !== selfId && (x.canApproveHours || x.canApproveLeaves || x.role === 'ADMIN' || x.role === 'COORDINATOR'));
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">— brak przełożonego —</option>
      {candidates.map(x => <option key={x.id} value={x.id}>{x.firstName} {x.lastName}</option>)}
    </select>
  );
}

// Sortowanie tabeli pracowników (T37)
const EMP_SORT = {
  employee: u => `${u.lastName} ${u.firstName}`,
  role: u => ROLE_LABELS[u.role] || u.role,
  employment: u => u.employmentType,
  approves: u => (u.canApproveEventHours ? 8 : 0) + (u.canApproveHours ? 4 : 0) + (u.canApproveLeaves ? 2 : 0) + (u.canSettleLeaves ? 1 : 0),
  position: u => u.position || null,
  leave: u => u.hasLeaveEntitlement === false ? null : leaveLeft(u),
  event: u => u.eventWorkEnabled ? 1 : 0,
  kamilkowa: u => u.eventWorkEnabled ? (u.hasKamilkowa ? 2 : 1) : 0,
  niekaralnosc: u => u.eventWorkEnabled ? (u.hasNiekaralnosc ? 2 : 1) : 0,
  license: u => u.drivingLicense || null,
  supervisor: u => u._supervisorName || null,
  createdAt: u => String(u.createdAt || ''),
};

export function EmployeesPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showProfile, setShowProfile] = useState(null);
  const [personCard, setPersonCard] = useState(null); // T91: karta „Eventy pracownika"
  const [showForm, setShowForm] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [formDirty, setFormDirty] = useState(false);       // T62: niezapisane dane w „Dodaj pracownika"
  const [profileDirty, setProfileDirty] = useState(false); // T62: niezapisane zmiany w profilu
  const [newPass, setNewPass] = useState('');
  const [filter, setFilter] = useState('STAFF'); // T137: domyślnie pracownicy stali (bez eventowych)
  const [loading, setLoading] = useState(true);
  const { show, ToastComponent } = useToast();

  const load = async () => { try { setUsers(await api.getUsers()); } catch (e) {} setLoading(false); };
  useEffect(() => { load(); }, []);

  const q = search.trim().toLowerCase();
  // Opiekun = przełożony (supervisorId) — nazwisko dokładane z tej samej listy
  const byId = Object.fromEntries(users.map(u => [u.id, u]));
  const withSup = users.map(u => ({ ...u, _supervisorName: u.supervisorId && byId[u.supervisorId] ? fullName(byId[u.supervisorId]) : null }));
  const filtered = withSup
    .filter(u => !filter || (filter === 'STAFF' ? u.role !== 'EVENT' : u.role === filter))
    .filter(u => !q || `${u.firstName} ${u.lastName} ${u.email || ''} ${u.position || ''}`.toLowerCase().includes(q));
  const us = useSort(filtered, EMP_SORT, 'employee', 'asc');

  const handleSave = async (data) => {
    try { await api.updateUser(showProfile.id, data); show('Zapisano!'); setShowProfile(null); setProfileDirty(false); load(); }
    catch (e) { show(e.message, 'error'); }
  };

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Pracownicy ({users.length})</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Dodaj pracownika</button>
      </div>
      <div className="filter-pills">
        {[['STAFF', `Stali (${users.filter(u => u.role !== 'EVENT').length})`], ['', `Wszyscy (${users.length})`], ['WAREHOUSE', 'Fizyczni'], ['EVENT', `Eventowi (${users.filter(u => u.role === 'EVENT').length})`], ['COORDINATOR', 'Koordynatorzy'], ['ADMINISTRATION', ROLE_LABELS.ADMINISTRATION]].map(([v, l]) => (
          <button key={v} className={`filter-pill ${filter === v ? 'active' : ''}`} onClick={() => setFilter(v)}>{l}</button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Szukaj: nazwisko, e-mail, stanowisko" style={{ marginLeft: 'auto', maxWidth: 240 }} />
      </div>
      <div className="card">
        {loading ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                <SortTh label="Pracownik" k="employee" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Rola" k="role" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Stanowisko" k="position" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Opiekun" k="supervisor" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Zatrudnienie" k="employment" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Zatwierdza" k="approves" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Urlop" k="leave" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Event" k="event" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Kamilkowa" k="kamilkowa" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Niekaralność" k="niekaralnosc" sort={us.sort} onSort={us.toggle} />
                <SortTh label="Pr. jazdy" k="license" sort={us.sort} onSort={us.toggle} />
                <SortTh label="W firmie od" k="createdAt" sort={us.sort} onSort={us.toggle} />
                <th>Akcje</th>
              </tr></thead>
              <tbody>
                {us.sorted.map(u => (
                  <tr key={u.id}>
                    <td><PersonCell name={fullName(u)} />{((u.lockStage || 0) >= 3 || (u.lockedUntil && new Date(u.lockedUntil) > new Date())) && <span title={(u.lockStage || 0) >= 3 ? 'Konto zablokowane na stałe — odblokuj w profilu' : 'Konto zablokowane czasowo po nieudanych logowaniach'} style={{ marginLeft: 4 }}>🔒</span>}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td style={{ fontSize: 10 }}>{u.position || <span style={{ color: '#CBD5E1' }}>—</span>}</td>
                    <td style={{ fontSize: 10 }}>{u._supervisorName || <span style={{ color: u.role === 'ADMIN' ? '#CBD5E1' : '#DC2626' }}>{u.role === 'ADMIN' ? '—' : 'brak'}</span>}</td>
                    <td style={{ fontSize: 10 }}>{u.employmentType === 'HOURLY' ? 'Godziny' : 'Etat'}</td>
                    <td style={{ fontSize: 10 }}>
                      {u.canApproveHours || u.canApproveLeaves || u.canSettleLeaves || u.canSettlePayroll || u.canApproveEventHours || u.canOrderSupplies || u.canAccounting
                        ? <span style={{ color: '#2563EB', fontWeight: 600 }}>
                            {[u.canApproveHours && 'godz.', u.canApproveEventHours && 'godz. eventowe', u.canApproveLeaves && 'urlopy', u.canSettleLeaves && 'rozlicza url.', u.canSettlePayroll && 'wypłaty', u.canOrderSupplies && 'zamawia', u.canAccounting && 'kadry'].filter(Boolean).join(' + ')}
                          </span>
                        : <span style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 10 }}>
                      {u.hasLeaveEntitlement === false
                        ? <span style={{ color: '#94A3B8' }}>nie dotyczy</span>
                        : <span>{fmtLeaveDays(leaveLeft(u))} / {u.leaveDaysTotal || 0}</span>}
                    </td>
                    <td>{u.eventWorkEnabled ? <span style={{ color: '#059669', fontSize: 10 }}>✓</span> : <span style={{ color: '#94A3B8', fontSize: 10 }}>—</span>}</td>
                    <td>{u.eventWorkEnabled ? (u.hasKamilkowa ? <Badge status="APPROVED" /> : <span style={{ color: '#DC2626', fontSize: 10 }}>BRAK</span>) : '—'}</td>
                    <td>{u.eventWorkEnabled ? (u.hasNiekaralnosc ? <Badge status="APPROVED" /> : <span style={{ color: '#DC2626', fontSize: 10 }}>BRAK</span>) : '—'}</td>
                    <td style={{ fontSize: 10, fontWeight: 600 }}>{u.drivingLicense || <span style={{ color: '#CBD5E1', fontWeight: 400 }}>—</span>}</td>
                    <td style={{ fontSize: 10 }} title="Data założenia konta w aplikacji">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('pl-PL') : '—'}</td>
                    <td>
                      <div className="flex gap-4">
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowProfile(u)}>Profil</button>
                        {(u.role === 'EVENT' || u.eventWorkEnabled) && <button className="btn btn-ghost btn-sm" title="Na jakich eventach był, godziny, wypłaty" onClick={() => setPersonCard(u)}>🎪</button>}
                        <button className="btn btn-outline btn-sm" onClick={() => { setResetFor(u); setNewPass(''); }}>Hasło</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {us.sorted.length === 0 && <tr><td colSpan={13} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak pracowników pasujących do filtra</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showProfile && (
        <Modal title={`${fullName(showProfile)}`} onClose={() => { setShowProfile(null); setProfileDirty(false); }} wide dirty={profileDirty}>
          <ProfileEditor user={showProfile} allUsers={users} onSave={handleSave} onClose={() => { setShowProfile(null); setProfileDirty(false); }}
            onDirtyChange={setProfileDirty}
            onUnlock={async () => { try { await api.unlockUser(showProfile.id); show('Konto odblokowane'); setShowProfile(null); load(); } catch (e) { show(e.message, 'error'); } }}
            onRequestProfile={async () => { if (!window.confirm('Pracownik przy następnym logowaniu będzie musiał ponownie wypełnić dane kontaktowe. Kontynuować?')) return; try { await api.requestProfile(showProfile.id); show('Poproszono o ponowne uzupełnienie'); setShowProfile(null); load(); } catch (e) { show(e.message, 'error'); } }}
            onDeactivate={async () => { await api.deleteUser(showProfile.id); show('Konto dezaktywowane'); setShowProfile(null); load(); }} />
        </Modal>
      )}
      {resetFor && (
        <Modal title={`Ustaw nowe hasło — ${fullName(resetFor)}`} onClose={() => setResetFor(null)} dirty={newPass.length > 0}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#64748B' }}>
              Nowe hasło zacznie obowiązywać od razu, a pracownik zostanie wylogowany ze wszystkich urządzeń.
              Przekaż mu je — przy logowaniu aplikacja poprosi go o ustawienie własnego.
            </div>
            <div className="input-group">
              <label>Nowe hasło (min. 10 znaków)</label>
              <input type="text" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="np. Start2026!" />
            </div>
            <button className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-start' }}
              onClick={() => setNewPass(Math.random().toString(36).slice(2, 10) + 'Aa1!')}>
              Wygeneruj losowe
            </button>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setResetFor(null)}>Anuluj</button>
              <button className="btn btn-primary" disabled={newPass.length < 10}
                onClick={async () => {
                  try {
                    const r = await api.resetUserPassword(resetFor.id, newPass);
                    show(r.message || 'Hasło zmienione');
                    setResetFor(null);
                  } catch (e) { show(e.message, 'error'); }
                }}>Zapisz hasło</button>
            </div>
          </div>
        </Modal>
      )}

      {personCard && <PersonEventsModal userId={personCard.id} name={fullName(personCard)} onClose={() => setPersonCard(null)} />}
      {showForm && (
        <Modal title="Dodaj pracownika" onClose={() => { setShowForm(false); setFormDirty(false); }} wide dirty={formDirty}>
          <NewEmployeeForm allUsers={users} initialRole={filter && filter !== 'STAFF' ? filter : undefined} onDirtyChange={setFormDirty}
            onSubmit={async (data) => { try { await api.createUser(data); show('Konto utworzone!'); setShowForm(false); setFormDirty(false); load(); } catch (e) { show(e.message, 'error'); } }}
            onClose={() => { setShowForm(false); setFormDirty(false); }} />
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}

function ProfileEditor({ user: u, allUsers = [], onSave, onClose, onDeactivate, onUnlock, onRequestProfile, onDirtyChange }) {
  const isLocked = (u.lockStage || 0) >= 3 || (u.lockedUntil && new Date(u.lockedUntil) > new Date());
  const lockText = (u.lockStage || 0) >= 3
    ? 'Konto zablokowane na stałe po powtarzających się błędnych logowaniach.'
    : `Konto zablokowane po nieudanych logowaniach (etap ${u.lockStage || 1}) — do ${u.lockedUntil ? new Date(u.lockedUntil).toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '?'}.`;
  // T120: podwładni (osoby z supervisorId = ta osoba) — do zmiany w profilu, zapis razem z resztą
  const subsOf = () => allUsers.filter(x => x.supervisorId === u.id).map(x => x.id).sort();
  const [data, setData] = useState(() => ({ ...u, drivingLicense: licenseToList(u.drivingLicense), managedUserIds: subsOf() }));
  const [initial] = useState(() => ({ ...u, drivingLicense: licenseToList(u.drivingLicense), managedUserIds: subsOf() }));
  const [subsQ, setSubsQ] = useState('');
  const byIdAll = Object.fromEntries(allUsers.map(x => [x.id, x]));
  const toggleSub = (x) => {
    const on = data.managedUserIds.includes(x.id);
    if (on) {
      if (x.role === 'ADMINISTRATION') { window.alert(`${fullName(x)} jest z Biura i musi mieć przełożonego — przenieś tę osobę pod kogoś innego (w jej profilu albo w „Strukturze firmy”).`); return; }
      set('managedUserIds', data.managedUserIds.filter(i => i !== x.id)); return;
    }
    const cur = x.supervisorId && byIdAll[x.supervisorId];
    if (cur && cur.id !== u.id && !window.confirm(`${fullName(x)} ma już przełożonego: ${fullName(cur)}.

Przenieść do: ${fullName(u)}?`)) return;
    set('managedUserIds', [...data.managedUserIds, x.id].sort());
  };
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  // T62: zgłaszamy rodzicowi, czy są niezapisane zmiany (✕ / Esc wtedy pytają)
  useEffect(() => { onDirtyChange && onDirtyChange(isDirty(data, initial)); /* eslint-disable-next-line */ }, [data]);
  // Zmiana roli na eventową odbiera urlop (zasada z CLAUDE.md); reszta uprawnień bez zmian
  const setRole = (role) => setData(d => ({
    ...d, role, hasLeaveEntitlement: role === 'EVENT' ? false : d.hasLeaveEntitlement,
    // T76: rodzaj pracy zgodny z rolą
    eventWorkEnabled: role === 'EVENT' ? true : d.eventWorkEnabled,
    warehouseWorkEnabled: role === 'WAREHOUSE' ? true : d.warehouseWorkEnabled,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {isLocked && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>🔒 {lockText}</span>
          {onUnlock && <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }} onClick={onUnlock}>Odblokuj teraz</button>}
        </div>
      )}
      <div className="card" style={{ background: '#F8FAFC' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700 }}>👤 Dane</h4>
        <div className="flex gap-8 flex-wrap">
          <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Imię</label><input value={data.firstName || ''} onChange={e => set('firstName', e.target.value)} /></div>
          <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Nazwisko</label><input value={data.lastName || ''} onChange={e => set('lastName', e.target.value)} /></div>
        </div>
        <div className="flex gap-8 flex-wrap" style={{ marginTop: 8 }}>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Email</label><input value={data.email} onChange={e => set('email', e.target.value)} /></div>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Telefon</label><input value={data.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
        </div>
        <div className="flex gap-8 flex-wrap" style={{ marginTop: 8 }}>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Rola</label>
            <select value={data.role} onChange={e => setRole(e.target.value)}>
              {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Stanowisko <span style={{ fontWeight: 400, color: '#94A3B8' }}>— opis funkcji w firmie, nie nadaje uprawnień</span></label><input list="positions-list" value={data.position || ''} onChange={e => set('position', e.target.value)} placeholder="np. Opiekun pracowników eventowych" /><PositionsList users={allUsers} /></div>
          <div className="input-group" style={{ minWidth: 120 }}><label>Płeć</label>
            <select value={data.gender || ''} onChange={e => set('gender', e.target.value || null)}>
              <option value="">—</option><option value="K">kobieta</option><option value="M">mężczyzna</option>
            </select>
          </div>
        </div>
        <div className="flex gap-8 flex-wrap" style={{ marginTop: 8 }}>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Przełożony{data.role === 'ADMINISTRATION' && <span style={{ color: '#DC2626' }}> * (Biuro — wymagany)</span>}</label>
            <SupervisorSelect value={data.supervisorId} onChange={v => set('supervisorId', v)} allUsers={allUsers} selfId={data.id} />
          </div>
          <div className="input-group flex-1" style={{ minWidth: 160 }}><label>Zatrudnienie</label>
            <select value={data.employmentType || 'HOURLY'} onChange={e => set('employmentType', e.target.value)}>
              <option value="HOURLY">Godziny</option><option value="SALARY">Etat</option>
            </select>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748B', marginTop: 6 }}>
          Przełożony zatwierdza godziny i urlopy tej osoby. Zmiana roli nie zmienia uprawnień do zatwierdzania — ustawia się je niżej.
        </div>
      </div>

      {(data.role === 'EVENT' || data.eventWorkEnabled) && ( // T128: tylko pracujący na eventach
      <div className="card" style={{ background: '#F8FAFC' }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700 }}>🆘 Dane kontaktowe i kryzysowe</h4>
        <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>
          {u.profileCompletedAt
            ? `Uzupełnione przez pracownika ${new Date(u.profileCompletedAt).toLocaleDateString('pl-PL')}.`
            : (u.role === 'EVENT' ? 'Jeszcze nieuzupełnione — pracownik wypełni je przy najbliższym logowaniu.' : 'Nieuzupełnione (wymagane tylko od eventowych; możesz wpisać ręcznie).')}
          {' '}Te dane nie trafiają do dziennika zdarzeń.
        </div>
        <div className="flex gap-8 flex-wrap">
          <div className="input-group flex-1" style={{ minWidth: 150 }}><label>Telefon kryzysowy</label><input value={data.emergencyPhone || ''} onChange={e => set('emergencyPhone', e.target.value)} placeholder="kto go obudzi" /></div>
          <div className="input-group flex-1" style={{ minWidth: 200 }}><label>Adres zamieszkania</label><input value={data.address || ''} onChange={e => set('address', e.target.value)} /></div>
        </div>
        <div className="flex gap-8 flex-wrap" style={{ marginTop: 8 }}>
          <div className="input-group" style={{ minWidth: 150 }}><label>Data urodzenia{data.birthDate && <span style={{ color: '#64748B', fontWeight: 400 }}> — {Math.floor((Date.now() - new Date(data.birthDate).getTime()) / (365.25 * 86400000))} lat</span>}</label><input type="date" value={(data.birthDate || '').slice(0, 10)} onChange={e => set('birthDate', e.target.value)} /></div>
          <div className="input-group" style={{ minWidth: 150 }}><label>Uczeń / student</label>
            <select value={data.studentStatus || ''} onChange={e => set('studentStatus', e.target.value || null)}>
              <option value="">— nie podano —</option><option value="NONE">Nie</option><option value="PUPIL">Uczeń</option><option value="STUDENT">Student</option>
            </select>
          </div>
          {u.role === 'EVENT' && onRequestProfile && (
            <div className="input-group" style={{ justifyContent: 'flex-end' }}><label>&nbsp;</label>
              <button className="btn btn-outline btn-sm" onClick={onRequestProfile} title="Przy następnym logowaniu pracownik znów przejdzie przez formularz">Poproś o ponowne uzupełnienie</button>
            </div>
          )}
        </div>
      </div>
      )}
      <div className="card" style={{ background: '#F8FAFC' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700 }}>💰 Rodzaj pracy i stawki</h4>
        <WorkKinds data={data} set={set} />
        {(data.eventWorkEnabled || data.role === 'EVENT') && (
          <div style={{ marginTop: 8, padding: 10, background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#92400E', marginBottom: 8 }}>Dokumenty eventowe</div>
            <Toggle on={data.hasKamilkowa} onChange={v => set('hasKamilkowa', v)} label="Ustawa Kamilkowa" />
            {data.hasKamilkowa && <div className="input-group" style={{ marginTop: 4 }}><label>Ważne do</label><input type="date" value={data.kamilkowaExpiryDate?.split('T')[0] || ''} onChange={e => set('kamilkowaExpiryDate', e.target.value)} /></div>}
            <Toggle on={data.hasNiekaralnosc} onChange={v => set('hasNiekaralnosc', v)} label="Niekaralność" />
            {data.hasNiekaralnosc && <div className="input-group" style={{ marginTop: 4 }}><label>Ważne do</label><input type="date" value={data.niekaralnoscExpiryDate?.split('T')[0] || ''} onChange={e => set('niekaralnoscExpiryDate', e.target.value)} /></div>}
          </div>
        )}
      </div>
      <div className="card" style={{ background: '#F8FAFC' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700 }}>🪪 Uprawnienia</h4>
        <LicensePicker value={data.drivingLicense} onChange={v => set('drivingLicense', v)} />
        {data.drivingLicense?.length > 0 && (
          <div className="input-group" style={{ maxWidth: 220, marginTop: 8 }}>
            <label>Prawo jazdy od roku{data.licenseSince && <span style={{ fontWeight: 400, color: '#64748B' }}> — {new Date().getFullYear() - Number(data.licenseSince)} lat za kierownicą</span>}</label>
            <input type="number" min="1960" max={new Date().getFullYear()} value={data.licenseSince || ''} onChange={e => set('licenseSince', e.target.value)} placeholder="np. 2018" />
          </div>
        )}
      </div>
      {(data.role === 'EVENT' || data.eventWorkEnabled) && (
        <div className="card" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
          <Toggle on={!!data.experienced} onChange={v => set('experienced', v)} label="★ Doświadczony"
            hint="Pracował u nas na eventach przed FunHR — filtr „sprawdzeni” przy układaniu składu pokaże go od razu, bez liczenia eventów w aplikacji. Pracownik nie widzi tego pola." />
        </div>
      )}
      <div className="card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700 }}>📝 Uwagi wewnętrzne</h4>
        <div style={{ fontSize: 10, color: '#92400E', marginBottom: 6 }}>
          Widzi admin oraz osoby układające składy eventów (przy wyborze ludzi do eventu) — pracownik <b>nie widzi</b> tego pola.
          Zapisuj <b>skutek organizacyjny</b>: „jeździ tylko z drugą osobą", „nie prowadzi po zmroku",
          „wymaga przerwy co 4 h". <b>Nie zapisuj informacji o zdrowiu ani diagnoz</b> (choroby, leki,
          uczulenia) — pracodawcy nie wolno ich przetwarzać (RODO, art. 9).
        </div>
        <textarea rows={3} value={data.internalNotes || ''} onChange={e => set('internalNotes', e.target.value)}
          placeholder="np. pierwszy raz busem — na początek z kimś doświadczonym" />
      </div>
      <div className="card" style={{ background: '#F8FAFC', border: '1px solid #BFDBFE' }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>🔑 Uprawnienia do zatwierdzania</h4>
        <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>
          Dotyczy wyłącznie osób, dla których ten pracownik jest przełożonym.
        </div>
        <Toggle on={!!data.canApproveHours} onChange={v => set('canApproveHours', v)}
          label="Może zatwierdzać godziny i dodatki" hint={PERM_HINTS.canApproveHours} />
        <Toggle on={!!data.canApproveLeaves} onChange={v => set('canApproveLeaves', v)}
          label="Może zatwierdzać urlopy" hint={PERM_HINTS.canApproveLeaves} />
        <Toggle on={!!data.canSettleLeaves} onChange={v => set('canSettleLeaves', v)}
          label="Rozlicza urlopy (widzi salda i zatwierdzone wnioski wszystkich, wprowadza je do Enovy)" hint={PERM_HINTS.canSettleLeaves} />
        <Toggle on={!!data.canSettlePayroll} onChange={v => set('canSettlePayroll', v)}
          label="Rozlicza wypłaty (ekran „Rozliczenia”: zamyka okresy do wypłaty wszystkich pracowników)" hint={PERM_HINTS.canSettlePayroll} />
        <Toggle on={!!data.canAccounting} onChange={v => set('canAccounting', v)}
          label="Kadry i księgowość (ekran „Kadry”: stawki wszystkich, pula urlopu; liczba dni i ewidencja pracy zdalnej)" hint={PERM_HINTS.canAccounting} />
        <Toggle on={!!data.canOrderSupplies} onChange={v => set('canOrderSupplies', v)}
          label="Zamawia zapotrzebowanie (widzi wszystkie zatwierdzone zgłoszenia, oznacza zamówione i dostarczone)" hint={PERM_HINTS.canOrderSupplies} />
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', marginBottom: 2 }}>🎪 Eventy</div>
          <Toggle on={!!data.canManageEvents} onChange={v => set('canManageEvents', v)}
            label="Zarządza eventami (tworzy, edytuje, zmienia status, układa skład)" hint={PERM_HINTS.canManageEvents} />
          <Toggle on={!!data.canAssignEvents} onChange={v => set('canAssignEvents', v)}
            label="Przypisuje do eventów (zmienia tylko skład istniejących)" hint={PERM_HINTS.canAssignEvents} />
          <Toggle on={!!data.canApproveEventHours} onChange={v => set('canApproveEventHours', v)}
            label="Zatwierdza godziny eventowe (wszystkich) — także wydatki pod eventami i dokumenty eventowe" hint={PERM_HINTS.canApproveEventHours} />
          {data.canApproveEventHours && (
            <div style={{ fontSize: 10, color: '#64748B', marginLeft: 4 }}>
              Widzi i zatwierdza wpisy „Event", dniówki i wszystko pod eventami u <b>każdego</b> pracownika (nie tylko podwładnych) — oprócz własnych.
              Godziny magazynowe zostają u przełożonego. Weryfikuje też Kamilkową i niekaralność osób pracujących na eventach.
            </div>
          )}
        </div>

        {(data.canApproveHours || data.canApproveLeaves) && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #E2E8F0' }}>
            <div className="input-group">
              <label><Hint text={PERM_HINTS.deputyId}>Zastępca (gdy ta osoba jest nieobecna)</Hint></label>
              <select value={data.deputyId || ''} onChange={e => set('deputyId', e.target.value || null)}>
                <option value="">— brak zastępcy —</option>
                {allUsers.filter(x => x.id !== data.id && (x.canApproveHours || x.canApproveLeaves || x.role === 'ADMIN'))
                  .map(x => <option key={x.id} value={x.id}>{x.firstName} {x.lastName}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 10, color: '#64748B', marginTop: 4 }}>
              Zastępca widzi i zatwierdza wnioski podwładnych tej osoby.
            </div>
          </div>
        )}
      </div>

      {(data.managedUserIds.length > 0 || data.canApproveHours || data.canApproveLeaves || ['COORDINATOR', 'ADMINISTRATION', 'ADMIN'].includes(data.role)) && (
        <div className="card" style={{ background: '#F8FAFC' }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700 }}>👥 Podwładni ({data.managedUserIds.length})</h4>
          <div style={{ fontSize: 10, color: '#64748B', marginBottom: 6 }}>
            Zaznaczone osoby mają tę osobę jako przełożonego (zatwierdza ich godziny i urlopy, jeśli ma uprawnienia wyżej). Zmiany zapiszą się przyciskiem „Zapisz”.
            {data.managedUserIds.length > 0 && !data.canApproveHours && !data.canApproveLeaves && data.role !== 'ADMIN' && <b style={{ color: '#B45309' }}> ⚠ Ma podwładnych, ale nie może nic zatwierdzać.</b>}
          </div>
          <input placeholder="Szukaj…" value={subsQ} onChange={e => setSubsQ(e.target.value)} style={{ maxWidth: 200, marginBottom: 6, fontSize: 11 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {allUsers.filter(x => x.id !== u.id && x.role !== 'ADMIN' && (!subsQ || fullName(x).toLowerCase().includes(subsQ.toLowerCase())))
              .sort((a, b) => (data.managedUserIds.includes(b.id) - data.managedUserIds.includes(a.id)) || `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl'))
              .map(x => {
                const sup = x.supervisorId && x.supervisorId !== u.id && byIdAll[x.supervisorId];
                return (
                  <label key={x.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.managedUserIds.includes(x.id)} onChange={() => toggleSub(x)} />
                    <span>{fullName(x)} <span style={{ color: '#94A3B8' }}>({ROLE_LABELS[x.role] || x.role}{sup ? `, przełożony: ${fullName(sup)}` : ''})</span></span>
                  </label>
                );
              })}
          </div>
        </div>
      )}

      <div className="card" style={{ background: '#F8FAFC' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700 }}>🏖️ Urlop</h4>
        <Toggle on={data.hasLeaveEntitlement !== false} onChange={v => set('hasLeaveEntitlement', v)}
          label="Przysługuje urlop wypoczynkowy" hint={PERM_HINTS.hasLeaveEntitlement} />
        {data.hasLeaveEntitlement !== false ? (
          <div className="flex gap-8 flex-wrap" style={{ marginTop: 8 }}>
            <div className="input-group flex-1" style={{ minWidth: 120 }}>
              <label><Hint text={PERM_HINTS.leaveDaysTotal}>Dni przysługujące</Hint></label>
              <input type="number" value={data.leaveDaysTotal ?? 26} onChange={e => set('leaveDaysTotal', e.target.value)} />
            </div>
            <div className="input-group flex-1" style={{ minWidth: 120 }}>
              <label><Hint text={PERM_HINTS.leaveDaysUsed}>Dni wykorzystane</Hint></label>
              <input type="number" value={data.leaveDaysUsed ?? 0} onChange={e => set('leaveDaysUsed', e.target.value)} />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 10, color: '#64748B', marginTop: 4 }}>
            Zakładka „Urlopy" jest ukryta w panelu tego pracownika.
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button className="btn btn-danger btn-sm" onClick={onDeactivate}>Dezaktywuj konto</button>
        <div className="flex gap-6"><button className="btn btn-outline" onClick={onClose}>Anuluj</button><button className="btn btn-primary" onClick={() => onSave(data)}>Zapisz</button></div>
      </div>
    </div>
  );
}

function NewEmployeeForm({ onSubmit, onClose, allUsers = [], initialRole, onDirtyChange }) {
  const [f, setF] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '', position: '', canApproveEventHours: false, warehouseWorkEnabled: false, role: initialRole || 'WAREHOUSE', employmentType: initialRole === 'EVENT' ? 'HOURLY' : 'SALARY', supervisorId: null, drivingLicense: [], rateWarehouse: '', rateEvent: '', rateNight: '', rateEventDaily: '', eventWorkEnabled: false, canApproveHours: false, canApproveLeaves: false, hasLeaveEntitlement: true, canSettleLeaves: false, canManageEvents: false, canAssignEvents: false, managedUserIds: [] });
  // touched = użytkownik coś zmienił (a nie tylko podpowiedź roli z filtra) — T62
  const [touched, setTouched] = useState(false);
  const set = (k, v) => { setTouched(true); setF(d => ({ ...d, [k]: v })); };
  useEffect(() => { onDirtyChange && onDirtyChange(touched); /* eslint-disable-next-line */ }, [touched]);
  // Rola z aktywnego filtra listy (T62) — ustawiamy też jej domyślne uprawnienia
  useEffect(() => { if (initialRole) setRole(initialRole); /* eslint-disable-next-line */ }, [initialRole]);

  // Koordynator / Biuro: lista pracowników, którymi ma zarządzać (ustawia im supervisorId).
  // Zaznaczenie kogoś, kto ma już opiekuna, wymaga potwierdzenia przeniesienia.
  const managesPeople = ['COORDINATOR', 'ADMINISTRATION'].includes(f.role) || f.canApproveHours || f.canApproveLeaves;
  const byId = Object.fromEntries(allUsers.map(u => [u.id, u]));
  const toggleManaged = (u) => {
    const on = f.managedUserIds.includes(u.id);
    if (on) { set('managedUserIds', f.managedUserIds.filter(id => id !== u.id)); return; }
    const current = u.supervisorId && byId[u.supervisorId];
    if (current) {
      const newName = `${f.firstName} ${f.lastName}`.trim() || 'nowego opiekuna z tego formularza';
      const ok = window.confirm(`${fullName(u)} ma już opiekuna: ${fullName(current)}.\n\nPrzenieść tę osobę od ${fullName(current)} do: ${newName}?`);
      if (!ok) return;
    }
    set('managedUserIds', [...f.managedUserIds, u.id]);
  };

  // Zmiana roli podpowiada sensowne domyślne ustawienia
  const setRole = (role) => setF(d => ({
    ...d, role,
    hasLeaveEntitlement: role !== 'EVENT',
    canApproveHours: role === 'COORDINATOR',
    canApproveLeaves: role === 'COORDINATOR',
    canSettleLeaves: role === 'ADMINISTRATION',
    canManageEvents: false, // T150: uprawnienia eventowe nadaje się ręcznie — także koordynatorowi
    canAssignEvents: false,
    // T76: przy nowym koncie przełącznik wymuszony przez rolę nie „przechodzi" na inną rolę
    eventWorkEnabled: role === 'EVENT' ? true : d.role === 'EVENT' ? false : d.eventWorkEnabled,
    warehouseWorkEnabled: role === 'WAREHOUSE' ? true : d.role === 'WAREHOUSE' ? false : d.warehouseWorkEnabled,
    employmentType: role === 'EVENT' ? 'HOURLY' : 'SALARY', // T105: etat; eventowy — godziny
  }));

  // Pola wymagane — bez nich backend odrzuci (T35)
  const Req = () => <span style={{ color: '#DC2626' }}> *</span>;
  const missing = [];
  if (!f.firstName.trim()) missing.push('imię');
  if (!f.lastName.trim()) missing.push('nazwisko');
  if (!f.email.trim()) missing.push('e-mail');
  if (f.password.length < 10) missing.push('hasło startowe (min. 10 znaków)');
  const ready = missing.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 11, color: '#64748B' }}>Pola oznaczone <span style={{ color: '#DC2626' }}>*</span> są wymagane — bez nich konto nie powstanie.</div>
      <div className="flex gap-8 flex-wrap"><div className="input-group flex-1" style={{ minWidth: 140 }}><label>Imię<Req /></label><input value={f.firstName} onChange={e => set('firstName', e.target.value)} /></div><div className="input-group flex-1" style={{ minWidth: 140 }}><label>Nazwisko<Req /></label><input value={f.lastName} onChange={e => set('lastName', e.target.value)} /></div></div>
      <div className="flex gap-8 flex-wrap"><div className="input-group flex-1" style={{ minWidth: 140 }}><label>Email<Req /></label><input type="email" value={f.email} onChange={e => set('email', e.target.value)} /></div><div className="input-group flex-1" style={{ minWidth: 140 }}><label>Hasło startowe<Req /> <span style={{ fontWeight: 400, color: '#94A3B8' }}>(min. 10 znaków)</span></label><input type="password" value={f.password} onChange={e => set('password', e.target.value)} /></div></div>
      <div className="flex gap-8 flex-wrap">
        <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Rola<Req /></label>
          <select value={f.role} onChange={e => { setTouched(true); setRole(e.target.value); }}>{ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </div>
        <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Stanowisko <span style={{ fontWeight: 400, color: '#94A3B8' }}>(opis, nie uprawnienia)</span></label><input list="positions-list" value={f.position} onChange={e => set('position', e.target.value)} placeholder="np. Kierowca, Księgowa" /><PositionsList users={allUsers} /></div>
        <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Telefon</label><input value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="opcjonalnie" /></div>
        <div className="input-group" style={{ minWidth: 120 }}><label>Płeć</label>
          <select value={f.gender || ''} onChange={e => set('gender', e.target.value || null)}>
            <option value="">—</option><option value="K">kobieta</option><option value="M">mężczyzna</option>
          </select>
        </div>
      </div>
      <div className="flex gap-8 flex-wrap">
        <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Przełożony{f.role === 'ADMINISTRATION' && <span style={{ color: '#DC2626' }}> * (Biuro — wymagany)</span>}</label>
          <SupervisorSelect value={f.supervisorId} onChange={v => set('supervisorId', v)} allUsers={allUsers} />
        </div>
        <div className="input-group flex-1" style={{ minWidth: 140 }}><label>Zatrudnienie</label><select value={f.employmentType} onChange={e => set('employmentType', e.target.value)}><option value="HOURLY">Godziny</option><option value="SALARY">Etat</option></select></div>
      </div>
      {!f.supervisorId && f.role !== 'ADMIN' && (
        <div style={{ fontSize: 10, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '6px 8px' }}>
          Bez przełożonego nikt poza adminem nie zobaczy ani nie zatwierdzi godzin i urlopów tej osoby.
        </div>
      )}
      <LicensePicker value={f.drivingLicense} onChange={v => set('drivingLicense', v)} />
      {managesPeople && (
        <div className="card" style={{ background: '#F8FAFC', padding: 12 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700 }}>Pracownicy, którymi zarządza ({f.managedUserIds.length})</h4>
          <div style={{ fontSize: 10, color: '#64748B', marginBottom: 6 }}>Zaznaczone osoby dostaną tę osobę jako opiekuna (przełożonego). Kto ma już opiekuna — zapytamy o przeniesienie.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {allUsers.filter(u => u.role !== 'ADMIN').sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl')).map(u => {
              const sup = u.supervisorId && byId[u.supervisorId];
              return (
                <label key={u.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.managedUserIds.includes(u.id)} onChange={() => toggleManaged(u)} />
                  <span>{fullName(u)} <span style={{ color: '#94A3B8' }}>({ROLE_LABELS[u.role] || u.role}{sup ? `, opiekun: ${fullName(sup)}` : ''})</span></span>
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div className="card" style={{ background: '#F8FAFC', padding: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700 }}>💰 Rodzaj pracy i stawki</h4>
        <WorkKinds data={f} set={set} />
      </div>

      <div className="card" style={{ background: '#F8FAFC', padding: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700 }}>Uprawnienia i urlop</h4>
        <Toggle on={f.canApproveHours} onChange={v => set('canApproveHours', v)} label="Może zatwierdzać godziny podwładnych" hint={PERM_HINTS.canApproveHours} />
        <Toggle on={f.canApproveLeaves} onChange={v => set('canApproveLeaves', v)} label="Może zatwierdzać urlopy podwładnych" hint={PERM_HINTS.canApproveLeaves} />
        <Toggle on={f.canSettleLeaves} onChange={v => set('canSettleLeaves', v)} label="Rozlicza urlopy (salda, zatwierdzone wnioski wszystkich → Enova)" hint={PERM_HINTS.canSettleLeaves} />
        <Toggle on={!!f.canSettlePayroll} onChange={v => set('canSettlePayroll', v)} label="Rozlicza wypłaty (ekran „Rozliczenia”)" hint={PERM_HINTS.canSettlePayroll} />
        <Toggle on={!!f.canOrderSupplies} onChange={v => set('canOrderSupplies', v)} label="Zamawia zapotrzebowanie" hint={PERM_HINTS.canOrderSupplies} />
        <Toggle on={!!f.canAccounting} onChange={v => set('canAccounting', v)} label="Kadry i księgowość (stawki, pula urlopu, praca zdalna)" hint={PERM_HINTS.canAccounting} />
        <Toggle on={f.canManageEvents} onChange={v => set('canManageEvents', v)} label="Zarządza eventami (tworzy i edytuje)" hint={PERM_HINTS.canManageEvents} />
        <Toggle on={f.canAssignEvents} onChange={v => set('canAssignEvents', v)} label="Przypisuje do eventów (tylko skład)" hint={PERM_HINTS.canAssignEvents} />
        <Toggle on={!!f.canApproveEventHours} onChange={v => set('canApproveEventHours', v)} label="Zatwierdza godziny eventowe (wszystkich) — opiekun pracowników eventowych" hint={PERM_HINTS.canApproveEventHours} />
        <Toggle on={f.hasLeaveEntitlement} onChange={v => set('hasLeaveEntitlement', v)} label="Przysługuje urlop wypoczynkowy" hint={PERM_HINTS.hasLeaveEntitlement} />
        {!f.hasLeaveEntitlement && (
          <div style={{ fontSize: 10, color: '#64748B' }}>Zakładka „Urlopy" nie pojawi się w panelu tej osoby.</div>
        )}
      </div>

      <div className="flex gap-6 justify-end" style={{ alignItems: 'center' }}>
        {!ready && <span style={{ fontSize: 11, color: '#B45309', marginRight: 'auto' }}>Brakuje: {missing.join(', ')}</span>}
        <button className="btn btn-outline" onClick={onClose}>Anuluj</button>
        <button className="btn btn-primary" onClick={() => onSubmit(f)} disabled={!ready}>Utwórz konto</button>
      </div>
    </div>
  );
}

// ========== DOCUMENTS ==========
// T54: zgłoszenia dokumentów do weryfikacji (admin: wszystkie; zatwierdzający godziny: podwładnych)
export function PendingDocuments({ onChange }) {
  const [rows, setRows] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const { show, ToastComponent } = useToast();
  const load = () => api.getPendingDocuments().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const act = async (fn, msg) => { try { const r = await fn(); show((r && r.message) || msg); load(); onChange && onChange(); } catch (e) { show(e.message, 'error'); } };
  const DOC = { KAMILKOWA: 'Ustawa Kamilkowa', NIEKARALNOSC: 'Niekaralność', STUDENT_ID: 'Legitymacja szkolna / studencka', DRIVING_LICENSE: 'Prawo jazdy' }; // T140
  if (!rows.length) return null;
  return (
    <div className="card" style={{ border: '1px solid #BFDBFE', background: '#EFF6FF' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#1E40AF' }}>🪪 Dokumenty do weryfikacji ({rows.length})</h3>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Pracownik</th><th>Dokument</th><th>Wydane / honorowane do</th><th>Skan</th><th>Zgłoszono</th><th>Akcja</th></tr></thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.id}>
                <td><PersonCell name={fullName(d.user)} /></td>
                <td>{DOC[d.docType] || d.docType}</td>
                <td style={{ fontSize: 11 }}>{d.issuedAt ? <>wydane {new Date(d.issuedAt).toLocaleDateString('pl-PL')}<br /></> : null}{d.validUntil ? <span style={{ color: '#64748B' }}>do {new Date(d.validUntil).toLocaleDateString('pl-PL')}</span> : '—'}</td>
                <td>{d.attachments?.length ? d.attachments.map(a => <button key={a.id} className="btn btn-ghost btn-sm" onClick={() => api.openAttachment(a.id).catch(e => show(e.message, 'error'))}>📎 podgląd</button>) : d.docType === 'DRIVING_LICENSE' ? <span style={{ color: '#64748B', fontSize: 10 }}>bez skanu (z zasady) — oryginał do wglądu</span> : <span style={{ color: '#DC2626', fontSize: 10 }}>brak skanu</span>}</td>
                <td>{new Date(d.createdAt).toLocaleDateString('pl-PL')}</td>
                <td><div className="flex gap-4">
                  <button className="btn btn-success btn-sm" title="Zatwierdź i wpisz do profilu" onClick={() => act(() => api.approveDocument(d.id), 'Zatwierdzone')}>✓</button>
                  <button className="btn btn-danger btn-sm" title="Odrzuć" onClick={() => { setRejecting(d); setNote(''); }}>✗</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rejecting && (
        <Modal title={`Odrzuć: ${DOC[rejecting.docType]} — ${fullName(rejecting.user)}`} onClose={() => setRejecting(null)} dirty={note.trim().length > 0}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="input-group"><label>Powód (pracownik go zobaczy)</label><textarea value={note} onChange={e => setNote(e.target.value)} placeholder="np. skan nieczytelny, brak daty ważności" /></div>
            <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={() => setRejecting(null)}>Anuluj</button><button className="btn btn-danger" disabled={note.trim().length < 3} onClick={() => { const d = rejecting; setRejecting(null); act(() => api.rejectDocument(d.id, note), 'Odrzucone'); }}>Odrzuć</button></div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}

export function DocumentsPage() {
  const [data, setData] = useState(null);
  const load = () => api.getDocumentsReport().then(setData).catch(console.error);
  useEffect(() => { load(); }, []);
  if (!data) return <p style={{ padding: 20, color: '#94A3B8' }}>Ładowanie...</p>;
  return (
    <div className="flex flex-col gap-14">
      <h2 className="page-title">Dokumenty pracowników</h2>
      <PendingDocuments onChange={load} />
      {data.alerts.length > 0 && (
        <div className="card" style={{ border: '1px solid #FCA5A5', background: '#FFF5F5' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#991B1B' }}>⚠️ Wymagana uwaga ({data.alerts.length})</h3>
          {data.alerts.map(u => <div key={u.id} style={{ fontSize: 11, padding: '4px 0', color: '#991B1B' }}>{u.firstName} {u.lastName} — {!u.hasKamilkowa && 'brak Kamilkowej'} {!u.hasNiekaralnosc && 'brak Niekaralności'}</div>)}
        </div>
      )}
      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Pracownik</th><th>Rola</th><th>Kamilkowa</th><th>Ważna do</th><th>Niekaralność</th><th>Ważna do</th><th>Pr. jazdy</th></tr></thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.id}>
                  <td><PersonCell name={`${u.firstName} ${u.lastName}`} /></td>
                  <td><RoleBadge role={u.role} /></td>
                  <td>{u.hasKamilkowa ? <Badge status="APPROVED" /> : <span style={{ color: '#DC2626', fontSize: 10 }}>BRAK</span>}</td>
                  <td style={{ fontSize: 10 }}>{u.kamilkowaExpiryDate ? new Date(u.kamilkowaExpiryDate).toLocaleDateString('pl-PL') : '—'}</td>
                  <td>{u.hasNiekaralnosc ? <Badge status="APPROVED" /> : <span style={{ color: '#DC2626', fontSize: 10 }}>BRAK</span>}</td>
                  <td style={{ fontSize: 10 }}>{u.niekaralnoscExpiryDate ? new Date(u.niekaralnoscExpiryDate).toLocaleDateString('pl-PL') : '—'}</td>
                  <td style={{ fontSize: 10, fontWeight: 600 }}>{u.drivingLicense || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ========== REPORTS ==========
export function ReportsPage() {
  const [report, setReport] = useState(null);
  const df = useDateFilter();
  const load = async () => { try { setReport(await api.getWeeklyReport(`from=${df.from}&to=${df.to}`)); } catch (e) {} };
  useEffect(() => { load(); }, []);

  // Ta sama tabela co na ekranie, do Excela (średnik jako separator — polski Excel)
  const exportCsv = () => {
    if (!report) return;
    const rows = [['Pracownik', 'Rola', 'Magazyn (h)', 'Event (h)', 'Noc (h)', 'Dniówki', 'Dodatki (zł)', 'Godziny razem', 'Kwota (zł)', 'Oczekuje (godz.)', 'Oczekuje (dod.)']];
    report.employees.forEach(e => rows.push([
      fullName(e.user), ROLE_LABELS[e.user.role] || e.user.role,
      e.warehouse, e.event, e.night, e.eventDaily, e.bonuses, e.totalHours, e.totalAmount.toFixed(2),
      e.pendingHours, e.pendingBonuses,
    ]));
    rows.push(['SUMA', '', '', '', '', report.totals.eventDaily || 0, '', report.totals.totalHours, report.totals.totalAmount.toFixed(2), report.totals.pendingHours, report.totals.pendingBonuses]);
    const csv = '\uFEFF' + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `raport_${df.from}_${df.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-14">
      <h2 className="page-title">Raporty</h2>
      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo} onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick} />
      <div className="flex gap-6">
        <button className="btn btn-primary" onClick={load}>Generuj raport</button>
        <button className="btn btn-outline" onClick={exportCsv} disabled={!report || !report.employees.length}>Pobierz CSV (Excel)</button>
      </div>
      {report && (
        <div className="card">
          <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>Raport: {new Date(report.period.from).toLocaleDateString('pl-PL')} – {new Date(report.period.to).toLocaleDateString('pl-PL')}</h3>
          <div className="flex gap-8 flex-wrap" style={{ marginBottom: 14 }}>
            <div className="card stat-card"><div className="stat-label">Łączne godziny</div><div className="stat-value" style={{ color: '#2563EB' }}>{report.totals.totalHours}h</div></div>
            <div className="card stat-card"><div className="stat-label">Dniówki</div><div className="stat-value" style={{ color: '#D97706' }}>{report.totals.eventDaily || 0}</div></div>
            <div className="card stat-card"><div className="stat-label">Łączny koszt</div><div className="stat-value" style={{ color: '#059669' }}>{report.totals.totalAmount.toFixed(0)} zł</div></div>
            <div className="card stat-card"><div className="stat-label">Do zatwierdzenia</div>
              <div className="stat-value" style={{ color: (report.totals.pendingHours + report.totals.pendingBonuses) > 0 ? '#D97706' : '#94A3B8' }}>{report.totals.pendingHours + report.totals.pendingBonuses}</div>
              <div className="stat-sub">nie wliczone do sum</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>Sumy obejmują wyłącznie zatwierdzone godziny i dodatki. Odrzucone są pominięte.</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Rola</th><th>Magazyn</th><th>Event</th><th>Noc</th><th>Dniówki</th><th>Dodatki</th><th>Godziny</th><th>Kwota</th><th>Oczekuje</th></tr></thead>
              <tbody>
                {report.employees.map(e => {
                  const pending = [];
                  if (e.pendingHours) pending.push(`${e.pendingHours} godz.`);
                  if (e.pendingBonuses) pending.push(`${e.pendingBonuses} dod.`);
                  return (
                    <tr key={e.user.id}>
                      <td><PersonCell name={fullName(e.user)} /></td>
                      <td><RoleBadge role={e.user.role} /></td>
                      <td>{e.warehouse > 0 ? `${e.warehouse}h` : '—'}</td>
                      <td>{e.event > 0 ? `${e.event}h` : '—'}</td>
                      <td>{e.night > 0 ? `${e.night}h` : '—'}</td>
                      <td>{e.eventDaily > 0 ? e.eventDaily : '—'}</td>
                      <td>{e.bonuses > 0 ? `${e.bonuses} zł` : '—'}</td>
                      <td><b>{e.totalHours}h</b></td>
                      <td><b>{e.totalAmount.toFixed(0)} zł</b></td>
                      <td style={{ color: '#D97706', fontSize: 11 }}>{pending.length ? pending.join(', ') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== SETTINGS ==========
const RATE_SETTINGS = [
  ['defaultRateWarehouse', 'Magazyn (zł/h)'], ['defaultRateEvent', 'Event (zł/h)'],
  ['defaultRateNight', 'Nocna (zł/h)'], ['defaultRateEventDaily', 'Dniówka (zł)'],
  ['defaultRateAnimation', 'Animacja (zł/h)'], ['defaultRateAnimationDaily', 'Animacja — dniówka (zł)'], // T143
];
// T80: ile miesięcy od wydania honorujemy zaświadczenia z KRK
const DOC_SETTINGS = [
  ['docValidityMonthsKamilkowa', 'Ustawa Kamilkowa (miesiące)'],
  ['docValidityMonthsNiekaralnosc', 'Niekaralność (miesiące)'],
];
// Limity roczne urlopów w dniach (T72/T73) — do ustawienia przez firmę
const LIMIT_SETTINGS = [
  ['limitOnDemandDays', 'Na żądanie', 'Część puli wypoczynkowej, nie dodatkowe dni (Kodeks: 4).'],
  ['limitChildcareDays', 'Opieka nad dzieckiem (art. 188)', 'Kodeks daje 2 dni albo 16 godzin w roku.'],
  ['limitCareAllowanceDays', 'Opieka nad chorym (zasiłek)', 'Górna granica zależy od sytuacji; ZUS pilnuje jej osobno.'],
  ['limitRemoteDays', 'Praca zdalna (okazjonalna)', 'Kodeks pracy: do 24 dni w roku (art. 67³³). 0 = bez limitu, np. gdy macie regulamin pracy zdalnej.'],
];
export function SettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { show, ToastComponent } = useToast();
  useEffect(() => {
    api.getSettings()
      .then(s => setForm({
        ...Object.fromEntries([...RATE_SETTINGS, ...LIMIT_SETTINGS, ...DOC_SETTINGS, ['confirmDeadlineHours']].map(([k]) => [k, s[k] ?? ''])),
        settlementsStartDate: s.settlementsStartDate ? String(s.settlementsStartDate).slice(0, 10) : '',
        // T156: integracja z DmuchApp — klucza serwer nie oddaje; puste pole = bez zmian
        dmuchappUrl: s.dmuchappUrl || '', dmuchappToken: '', dmuchappSyncMinutes: s.dmuchappSyncMinutes ?? 0,
        dmuchappTokenSet: !!s.dmuchappTokenSet, dmuchappLastSync: s.dmuchappLastSync, dmuchappLastResult: s.dmuchappLastResult,
      }))
      .catch(e => show(e.message, 'error'));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { dmuchappTokenSet, dmuchappLastSync, dmuchappLastResult, ...data } = form;
      const r = await api.updateSettings(data);
      setForm(f => ({ ...f, dmuchappToken: '', dmuchappTokenSet: !!r?.dmuchappTokenSet }));
      show('Ustawienia zapisane');
    }
    catch (e) { show(e.message, 'error'); }
    setSaving(false);
  };
  return (
    <div className="flex flex-col gap-14">
      <h2 className="page-title">Ustawienia</h2>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Domyślne stawki</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          Używane, gdy nowy pracownik nie ma wpisanej stawki (wtedy dostaje ją na stałe) oraz przy liczeniu wpisu
          godzin u kogoś, kto ma pustą stawkę (wtedy licznik bierze wartość stąd zamiast zera). Puste pole = brak domyślnej.
        </div>
        {!form ? <p style={{ color: '#94A3B8' }}>Ładowanie...</p> : (
          <>
            <div className="flex gap-8 flex-wrap">
              {RATE_SETTINGS.map(([k, label]) => (
                <div key={k} className="input-group flex-1" style={{ minWidth: 100 }}><label>{label}</label>
                  <input type="number" min="0" step="0.5" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder="brak" /></div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>Przycisk „Zapisz ustawienia" na dole zapisuje stawki i limity.</div>
          </>
        )}
      </div>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Limity urlopowe (dni w roku)</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          Ile dni danego typu pracownik może wykorzystać w roku kalendarzowym. Liczone po dacie rozpoczęcia;
          wnioski oczekujące też się liczą. <b>0 = bez limitu.</b> Urlop wypoczynkowy ma osobną pulę przy pracowniku (26/20 dni).
        </div>
        {!form ? <p style={{ color: '#94A3B8' }}>Ładowanie...</p> : (
          <div className="flex gap-8 flex-wrap">
            {LIMIT_SETTINGS.map(([k, label, hint]) => (
              <div key={k} className="input-group flex-1" style={{ minWidth: 200 }}>
                <label>{label}</label>
                <input type="number" min="0" max="365" step="1" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} />
                <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{hint}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Zaświadczenia z KRK — ważność od daty wydania</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          Zaświadczenia nie mają daty ważności. Ile miesięcy od wydania je honorujemy (0 = bezterminowo). Starszych
          pracownik nie wyśle, a w składzie eventu po tym czasie pokaże się ⚠.
        </div>
        {form && (
          <div className="flex gap-8 flex-wrap">
            {DOC_SETTINGS.map(([k, label]) => (
              <div key={k} className="input-group" style={{ minWidth: 200 }}><label>{label}</label>
                <input type="number" min="0" max="120" step="1" value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
          </div>
        )}
      </div>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Eventy — potwierdzanie udziału</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          Do ilu godzin przed startem eventu pracownik może potwierdzić albo odwołać udział. Po tym czasie przyciski znikają — zmiany tylko
          telefonicznie u koordynatora. 0 = bez terminu. Rozliczenie godzin pracownik może zrobić od 2 h po zakończeniu eventu.
        </div>
        {form && (
          <div className="input-group" style={{ maxWidth: 260 }}><label>Termin (godziny przed startem)</label>
            <input type="number" min="0" max="720" step="1" value={form.confirmDeadlineHours} onChange={e => setForm({ ...form, confirmDeadlineHours: e.target.value })} /></div>
        )}
      </div>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>Rozliczenia (wypłaty) — start</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          Wpisy sprzed tej daty nie liczą się do „do wypłaty" (zostały rozliczone poza aplikacją). Ustaw raz, przy
          pierwszym rozliczeniu w aplikacji. Puste = liczymy wszystko.
        </div>
        {form && (
          <div className="input-group" style={{ maxWidth: 220 }}><label>Rozliczenia liczymy od dnia</label>
            <input type="date" value={form.settlementsStartDate} onChange={e => setForm({ ...form, settlementsStartDate: e.target.value })} /></div>
        )}
      </div>
      <div className="card">
        <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>🗺 Integracja z DmuchApp — grupy wyjazdowe jako eventy</h3>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
          FunHR pobiera grupy z planowania tras w DmuchApp: <b>1 grupa = 1 event</b> (dzień, wyjazd, miejsca, atrakcje, uwagi dla ekipy).
          Ludzi nie pobieramy — skład układasz w FunHR. Klucz generujesz w DmuchApp: ⚙️ Panel → 🔗 FunHR. Pobieranie ręczne: przycisk
          „⟳ DmuchApp” w Eventach.
        </div>
        {form && (<>
          <div className="flex gap-8 flex-wrap">
            <div className="input-group flex-1" style={{ minWidth: 240 }}><label>Adres DmuchApp</label>
              <input value={form.dmuchappUrl} onChange={e => setForm({ ...form, dmuchappUrl: e.target.value })} placeholder="np. https://mapa.funandplay.pl" /></div>
            <div className="input-group flex-1" style={{ minWidth: 240 }}><label>Klucz z DmuchApp {form.dmuchappTokenSet && <span style={{ color: '#059669' }}>✓ zapisany</span>}</label>
              <input type="password" autoComplete="off" value={form.dmuchappToken} onChange={e => setForm({ ...form, dmuchappToken: e.target.value })} placeholder={form.dmuchappTokenSet ? 'zapisany — wklej nowy, aby zmienić' : 'wklej klucz z DmuchApp'} /></div>
            <div className="input-group" style={{ width: 200 }}><label>Pobieraj co (minut)</label>
              <input type="number" min="0" max="1440" step="5" value={form.dmuchappSyncMinutes} onChange={e => setForm({ ...form, dmuchappSyncMinutes: e.target.value })} />
              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>0 = tylko ręcznie (przycisk)</div></div>
          </div>
          {form.dmuchappLastSync && <div style={{ fontSize: 11, color: '#64748B', marginTop: 6 }}>Ostatnie pobranie: {new Date(form.dmuchappLastSync).toLocaleString('pl-PL')} — {form.dmuchappLastResult}</div>}
        </>)}
      </div>
      <div className="flex justify-end">
        <button className="btn btn-primary" onClick={save} disabled={saving || !form}>{saving ? 'Zapisywanie…' : 'Zapisz ustawienia'}</button>
      </div>
      <div className="card" style={{ color: '#94A3B8', fontSize: 12 }}>
        Powiadomienia e-mail: raport tygodniowy wysyła się automatycznie w poniedziałek, jeśli na serwerze skonfigurowano SMTP. Inne powiadomienia — w planach.
      </div>
      {ToastComponent}
    </div>
  );
}

// ========== DZIENNIK ZDARZEN (AUDIT LOG) ==========
const ACTION_LABELS = {
  CREATE_LEAVE: ['Złożono wniosek urlopowy', '#2563EB'],
  APPROVE_LEAVE: ['Zatwierdzono urlop', '#059669'],
  REJECT_LEAVE: ['Odrzucono urlop', '#DC2626'],
  APPROVE_HOURS: ['Zatwierdzono godziny', '#059669'],
  REJECT_HOURS: ['Odrzucono godziny', '#DC2626'],
  APPROVE_HOURS_BULK: ['Zatwierdzono godziny (zbiorczo)', '#059669'],
  APPROVE_BONUS_BULK: ['Zatwierdzono dodatki (zbiorczo)', '#059669'],
  APPROVE_BONUS: ['Zatwierdzono dodatek', '#059669'],
  APPROVE_SUPPLY: ['Zatwierdzono zapotrzebowanie', '#059669'],
  CHANGE_SUPPLY_STATUS: ['Zmiana statusu zapotrzebowania', '#0891B2'],
  CHANGE_LEAVE_POOL: ['Zmieniono pulę urlopu', '#0891B2'],
  CHANGE_SUBORDINATES: ['Zmieniono podwładnych', '#0891B2'],
  CHANGE_SETTLEMENT_METHOD: ['Zmieniono formę wypłaty', '#7C3AED'],
  REJECT_SUPPLY: ['Odrzucono zapotrzebowanie', '#DC2626'],
  ORDER_SUPPLY: ['Zamówiono zapotrzebowanie', '#7C3AED'],
  UPDATE_SUPPLY_ORDER: ['Zmieniono termin zamówienia', '#7C3AED'],
  DELIVER_SUPPLY: ['Zapotrzebowanie dostarczone', '#0369A1'],
  EDIT_SUPPLY: ['Poprawiono zapotrzebowanie', '#0891B2'],
  WITHDRAW_SUPPLY: ['Wycofano zapotrzebowanie', '#64748B'],
  EDIT_EVENT: ['Zmieniono event / skład', '#0891B2'],
  CHANGE_EVENT_STATUS: ['Zmiana statusu eventu', '#0891B2'],
  DELETE_EVENT: ['Usunięto event', '#DC2626'],
  ACCOUNT_LOCKED: ['Konto zablokowane (błędne logowania)', '#DC2626'],
  SEND_MESSAGE: ['Wysłano wiadomość', '#2563EB'],
  CREATE_SETTLEMENT: ['Rozliczono wypłatę', '#7C3AED'],
  REVERT_SETTLEMENT: ['Cofnięto rozliczenie wypłaty', '#DC2626'],
  ADD_ATTACHMENT: ['Dodano załącznik', '#0891B2'],
  DELETE_ATTACHMENT: ['Usunięto załącznik', '#DC2626'],
  SUBMIT_DOCUMENT: ['Pracownik zgłosił dokument', '#2563EB'],
  APPROVE_DOCUMENT: ['Zatwierdzono dokument (wpis do profilu)', '#059669'],
  REJECT_DOCUMENT: ['Odrzucono dokument', '#DC2626'],
  PROFILE_COMPLETED: ['Pracownik uzupełnił dane kontaktowe', '#059669'],
  PROFILE_UPDATED_SELF: ['Pracownik poprawił dane kontaktowe', '#0891B2'],
  LICENSE_UPDATED_SELF: ['Pracownik poprawił prawo jazdy', '#0891B2'],
  PROFILE_RECHECK_REQUESTED: ['Poproszono o ponowne uzupełnienie danych', '#D97706'],
  ACCOUNT_UNLOCKED: ['Konto odblokowane przez admina', '#059669'],
  UPDATE_SETTINGS: ['Zmiana domyślnych stawek', '#7C3AED'],
  MIGRATION_CAN_SETTLE_LEAVES: ['Aktualizacja 1.2: nadano „rozlicza urlopy" Biuru', '#64748B'],
  CREATE_EVENT: ['Utworzono event', '#D97706'],
  CREATE_USER: ['Utworzono konto', '#7C3AED'],
  UPDATE_USER: ['Zmieniono dane pracownika', '#F59E0B'],
  DEACTIVATE_USER: ['Dezaktywowano konto', '#DC2626'],
  RESET_PASSWORD: ['Zmieniono hasło pracownikowi', '#F59E0B'],
  CHANGE_OWN_PASSWORD: ['Zmiana własnego hasła', '#64748B'],
  PROCESS_LEAVE: ['Przyjęto urlop do rozliczenia', '#0891B2'],
  REJECT_BONUS: ['Odrzucono dodatek', '#DC2626'],
  EDIT_HOURS: ['Przełożony zmienił wpis godzin', '#F59E0B'],
  DELETE_HOURS: ['Przełożony usunął wpis godzin', '#DC2626'],
  EDIT_BONUS: ['Przełożony zmienił dodatek', '#F59E0B'],
  DELETE_BONUS: ['Przełożony usunął dodatek', '#DC2626'],
  APP_UPDATED: ['Aktualizacja aplikacji', '#7C3AED'],
  EDIT_LEAVE: ['Zmieniono wniosek urlopowy', '#F59E0B'],
  CANCEL_LEAVE: ['Wycofano wniosek urlopowy', '#DC2626'],
};

const LEAVE_TYPE_PL = Object.fromEntries(Object.entries(LEAVE_LABELS).map(([k, v]) => [k, v.toLowerCase()]));

function describeDetails(action, d) {
  if (!d) return null;
  const dt = (v) => v ? new Date(v).toLocaleDateString('pl-PL') : '';
  if (['CREATE_LEAVE', 'APPROVE_LEAVE', 'REJECT_LEAVE'].includes(action)) {
    const parts = [];
    if (d.typ) parts.push(`urlop ${LEAVE_TYPE_PL[d.typ] || d.typ}`);
    if (d.od && d.do) parts.push(`${dt(d.od)} – ${dt(d.do)}`);
    if (d.dni) parts.push(`${d.dni} dni`);
    let txt = parts.join(', ');
    if (d.komentarz) txt += ` · komentarz: „${d.komentarz}"`;
    if (d.powod) txt += ` · powód: „${d.powod}"`;
    return txt;
  }
  if (action.includes('HOURS')) {
    const p = [];
    if (d.data) p.push(dt(d.data));
    if (d.typ) p.push(d.typ);
    if (d.godziny) p.push(`${d.godziny}h`);
    if (d.kwota) p.push(`${Math.round(Number(d.kwota))} zł`);
    if (d.liczba) p.push(`${d.liczba} wpisów`);
    return p.join(', ');
  }
  if (action === 'APPROVE_BONUS') return `${d.rodzaj || ''} ${d.kwota ? Math.round(Number(d.kwota)) + ' zł' : ''}`.trim();
  if (action.includes('SUPPLY')) return [d.nazwa, d.ilosc, d.nowyStatus].filter(Boolean).join(', ');
  if (action === 'CREATE_EVENT') return [d.nazwa, d.data ? dt(d.data) : '', d.lokalizacja].filter(Boolean).join(', ');
  if (action === 'CREATE_USER') return [d.firstName, d.lastName, d.email, d.role].filter(Boolean).join(' · ');
  if (action === 'UPDATE_USER') return Array.isArray(d) ? `zmienione pola: ${d.join(', ')}` : null;
  return null;
}

// T172: dziennik logowań (90 dni) — kto, kiedy, skąd; nieudane próby na czerwono
const LOGIN_REASONS = { BAD_PASSWORD: 'złe hasło', UNKNOWN: 'nieznany e-mail', INACTIVE: 'konto nieaktywne', LOCKED: 'konto zablokowane', IP_LIMIT: 'limit prób z adresu' };
const shortAgent = (ua = '') => {
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  const br = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : ua ? 'inna' : '';
  return [br, os].filter(Boolean).join(' · ') || '—';
};
function LoginLog({ from, to }) {
  const [rows, setRows] = useState([]);
  const [failedOnly, setFailedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (failedOnly) p.set('failed', '1');
    setLoading(true);
    api.getLoginLog(p.toString()).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [from, to, failedOnly]);
  const failed = rows.filter(r => !r.success).length;
  return (
    <div className="card">
      <div className="flex justify-between items-center flex-wrap" style={{ gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#64748B' }}>Logowania z ostatnich 90 dni. Nieudanych w widoku: <b style={{ color: failed ? '#DC2626' : '#059669' }}>{failed}</b></div>
        <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={failedOnly} onChange={e => setFailedOnly(e.target.checked)} /> tylko nieudane</label>
      </div>
      {loading ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Data i godzina</th><th>Kto</th><th>Wynik</th><th>Adres IP</th><th>Urządzenie</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 10 }}>{new Date(r.createdAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  <td>{r.user ? <PersonCell name={`${r.user.firstName} ${r.user.lastName}`} /> : <span style={{ fontSize: 10, color: '#64748B' }}>{r.email}</span>}</td>
                  <td>{r.success ? <span style={{ color: '#059669', fontWeight: 600, fontSize: 11 }}>✓ zalogowano</span> : <span style={{ color: '#DC2626', fontWeight: 600, fontSize: 11 }}>✗ {LOGIN_REASONS[r.reason] || 'nieudane'}</span>}</td>
                  <td style={{ fontSize: 10, color: '#475569' }}>{r.ip || '—'}</td>
                  <td style={{ fontSize: 10, color: '#475569' }} title={r.userAgent || ''}>{shortAgent(r.userAgent)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak logowań w wybranym okresie</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AuditPage() {
  const [tab, setTab] = useState('events'); // T172: „Zdarzenia” | „Logowania”
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const df = useDateFilter();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (df.from) p.set('from', df.from);
      if (df.to) p.set('to', df.to);
      if (action) p.set('action', action);
      setLogs(await api.getAuditLog(p.toString()));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [df.from, df.to, action]);

  useEffect(() => { load(); }, [load]);

  const QUICK = [
    ['', 'Wszystko'],
    ['CREATE_LEAVE', 'Złożone urlopy'],
    ['APPROVE_LEAVE', 'Zatwierdzone urlopy'],
    ['REJECT_LEAVE', 'Odrzucone urlopy'],
    ['APPROVE_HOURS', 'Godziny'],
    ['UPDATE_USER', 'Zmiany pracowników'],
  ];

  const exportCsv = () => {
    const rows = [['Data', 'Kto', 'Rola', 'Zdarzenie', 'Szczegóły']];
    logs.forEach(l => rows.push([
      new Date(l.createdAt).toLocaleString('pl-PL'),
      l.autor ? `${l.autor.firstName} ${l.autor.lastName}` : l.userId,
      l.autor?.role || '',
      (ACTION_LABELS[l.action] || [l.action])[0],
      describeDetails(l.action, l.details) || '',
    ]));
    const csv = '\uFEFF' + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `dziennik_${df.from}_${df.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Dziennik zdarzeń</h2>
        {tab === 'events' && <button className="btn btn-outline" onClick={exportCsv} disabled={!logs.length}>Eksport CSV</button>}
      </div>

      <div className="filter-pills">
        <button className={`filter-pill ${tab === 'events' ? 'active' : ''}`} onClick={() => setTab('events')}>📋 Zdarzenia</button>
        <button className={`filter-pill ${tab === 'logins' ? 'active' : ''}`} onClick={() => setTab('logins')}>🔐 Logowania</button>
      </div>

      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo}
        onFilter={load} quickFilter={df.quick} onQuickFilter={df.handleQuick} />

      {tab === 'logins' ? <LoginLog from={df.from} to={df.to} /> : <>

      <div className="filter-pills">
        {QUICK.map(([v, l]) => (
          <button key={v} className={`filter-pill ${action === v ? 'active' : ''}`} onClick={() => setAction(v)}>{l}</button>
        ))}
      </div>

      <div className="card">
        {loading ? <p style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</p> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Data i godzina</th><th>Kto</th><th>Zdarzenie</th><th>Szczegóły</th></tr></thead>
              <tbody>
                {logs.map(l => {
                  const [label, color] = ACTION_LABELS[l.action] || [l.action, '#64748B'];
                  return (
                    <tr key={l.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 10 }}>
                        {new Date(l.createdAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>{l.autor ? <PersonCell name={`${l.autor.firstName} ${l.autor.lastName}`} /> : <span style={{ color: '#94A3B8', fontSize: 10 }}>konto usunięte</span>}</td>
                      <td><span style={{ color, fontWeight: 600, fontSize: 11 }}>{label}</span></td>
                      <td style={{ fontSize: 10, color: '#475569' }}>{describeDetails(l.action, l.details) || '—'}</td>
                    </tr>
                  );
                })}
                {logs.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak zdarzeń w wybranym okresie</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}

      <div className="card" style={{ background: '#F8FAFC', fontSize: 11, color: '#64748B' }}>
        Wpisy w dzienniku są zapisywane automatycznie i nie da się ich zmienić ani usunąć z poziomu aplikacji.
        Pozostają nawet po dezaktywacji konta pracownika.
      </div>
    </div>
  );
}

// ========== MOJE KONTO ==========
// T47: pracownik może poprawić dane kontaktowe w "Moje konto" (ten sam endpoint co przy pierwszym logowaniu)
// T107: prawo jazdy — eventowy i każdy z pracą na eventach (skład eventu filtruje po kategoriach)
const licenseText = (u) => u.drivingLicense ? `${u.drivingLicense}${u.licenseSince ? ` (od ${u.licenseSince})` : ''}` : 'brak';
function LicenseFields({ f, setF }) {
  return (
    <>
      <LicensePicker value={f.drivingLicense} onChange={v => setF(p => ({ ...p, drivingLicense: v }))} />
      {f.drivingLicense.length > 0 && (
        <div className="input-group" style={{ maxWidth: 220 }}><label>Prawo jazdy od roku</label>
          <input type="number" min="1960" max={new Date().getFullYear()} value={f.licenseSince} onChange={e => setF(p => ({ ...p, licenseSince: e.target.value }))} placeholder="np. 2018" /></div>
      )}
    </>
  );
}
const licenseForm = (u) => ({ drivingLicense: licenseToList(u.drivingLicense), licenseSince: u.licenseSince || '' });

function ContactDataCard() {
  const { user, refreshUser } = useAuth();
  const { show, ToastComponent } = useToast();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const open = () => { setF({ phone: user.phone || '', emergencyPhone: user.emergencyPhone || '', address: user.address || '', birthDate: user.birthDate ? String(user.birthDate).slice(0, 10) : '', studentStatus: user.studentStatus || '', gender: user.gender || '', ...licenseForm(user) }); setEdit(true); };
  const save = async () => {
    setBusy(true);
    try {
      const { drivingLicense, licenseSince, ...contact } = f;
      await api.completeProfile(contact);
      await api.updateMyLicense({ drivingLicense, licenseSince });
      await refreshUser(); show('Dane zapisane'); setEdit(false);
    }
    catch (e) { show(e.message, 'error'); }
    setBusy(false);
  };
  const STUD = { NONE: 'nie', PUPIL: 'uczeń', STUDENT: 'student' };
  return (
    <div className="card">
      <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700 }}>🆘 Dane kontaktowe i kryzysowe</h3>
      {!edit ? (
        <>
          <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', color: '#334155' }}>
            <span style={{ color: '#64748B' }}>Telefon</span><span>{user.phone || '—'}</span>
            <span style={{ color: '#64748B' }}>Numer kryzysowy</span><span>{user.emergencyPhone || '—'}</span>
            <span style={{ color: '#64748B' }}>Adres</span><span>{user.address || '—'}</span>
            <span style={{ color: '#64748B' }}>Data urodzenia</span><span>{user.birthDate ? new Date(user.birthDate).toLocaleDateString('pl-PL') : '—'}</span>
            <span style={{ color: '#64748B' }}>Uczeń / student</span><span>{STUD[user.studentStatus] || '—'}</span>
            <span style={{ color: '#64748B' }}>Płeć</span><span>{{ K: 'kobieta', M: 'mężczyzna' }[user.gender] || '—'}</span>
            <span style={{ color: '#64748B' }}>Prawo jazdy</span><span>{licenseText(user)}</span>
          </div>
          <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }} onClick={open}>Popraw dane</button>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="flex gap-8 flex-wrap">
            <div className="input-group flex-1" style={{ minWidth: 150 }}><label>Telefon</label><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
            <div className="input-group flex-1" style={{ minWidth: 150 }}><label>Numer kryzysowy</label><input value={f.emergencyPhone} onChange={e => setF({ ...f, emergencyPhone: e.target.value })} /></div>
          </div>
          <div className="input-group"><label>Adres zamieszkania</label><input value={f.address} onChange={e => setF({ ...f, address: e.target.value })} /></div>
          <div className="flex gap-8 flex-wrap">
            <div className="input-group flex-1" style={{ minWidth: 150 }}><label>Data urodzenia</label><input type="date" value={f.birthDate} onChange={e => setF({ ...f, birthDate: e.target.value })} /></div>
            <div className="input-group flex-1" style={{ minWidth: 150 }}><label>Uczeń / student</label>
              <select value={f.studentStatus} onChange={e => setF({ ...f, studentStatus: e.target.value })}><option value="">— wybierz —</option><option value="PUPIL">Uczeń</option><option value="STUDENT">Student</option><option value="NONE">Nie</option></select>
            </div>
            <div className="input-group flex-1" style={{ minWidth: 120 }}><label>Płeć</label>
              <select value={f.gender} onChange={e => setF({ ...f, gender: e.target.value })}><option value="">—</option><option value="K">kobieta</option><option value="M">mężczyzna</option></select>
            </div>
          </div>
          <LicenseFields f={f} setF={setF} />
          <div className="flex gap-6 justify-end"><button className="btn btn-outline btn-sm" onClick={() => setEdit(false)}>Anuluj</button><button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>Zapisz</button></div>
        </div>
      )}
      {ToastComponent}
    </div>
  );
}

// T83: własne stawki (tylko do odczytu). Pusta stawka → domyślna z Ustawień z dopiskiem.
function MyRatesCard({ user }) {
  const d = user.defaultRates || {};
  const rows = [];
  // T76: tylko stawki rodzajów pracy, które ta osoba ma włączone; dniówka u każdego z pracą na eventach (T135)
  const eventy = user.role === 'EVENT' || user.eventWorkEnabled;
  const magazyn = user.role === 'WAREHOUSE' || !!user.warehouseWorkEnabled;
  if (magazyn) rows.push(user.role === 'WAREHOUSE'
    ? ['Nadgodziny — magazyn', user.rateWarehouse, d.rateWarehouse, 'zł/h']
    : ['Magazyn', user.rateWarehouse, d.rateWarehouse, 'zł/h'],
    user.role === 'WAREHOUSE'
      ? ['Godziny nocne (zawsze płatne)', user.rateNight, d.rateNight, 'zł/h']
      : ['Nocna (magazyn)', user.rateNight, d.rateNight, 'zł/h']);
  if (eventy) rows.push(['Event', user.rateEvent, d.rateEvent, 'zł/h']);
  if (eventy && user.animationWorkEnabled) rows.push(['Animacja', user.rateAnimation, d.rateAnimation, 'zł/h'], ['Animacja — dniówka', user.rateAnimationDaily, d.rateAnimationDaily, 'zł']); // T143
  if (eventy) rows.push(['Dniówka', user.rateEventDaily, d.rateEventDaily, 'zł']); // T135
  if (!rows.length) return null;
  return (
    <div className="card">
      <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>💰 Moje stawki</h3>
      <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px' }}>
        {rows.map(([label, own, def, unit]) => (
          <React.Fragment key={label}>
            <span style={{ color: '#64748B' }}>{label}</span>
            <span>
              {own != null && own !== '' ? <b>{Number(own).toFixed(2)} {unit}</b>
                : def != null ? <>{Number(def).toFixed(2)} {unit} <span style={{ color: '#94A3B8', fontSize: 10 }}>(domyślna)</span></>
                : <span style={{ color: '#94A3B8' }}>ustala biuro</span>}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 6 }}>Stawki ustala firma. Jeśli coś się nie zgadza — napisz do przełożonego.</div>
    </div>
  );
}

/** T90/T91: moje wypłaty (za co) i moje eventy — pracownik widzi tylko swoje. */
function MyPayoutsCard({ user }) {
  const [hist, setHist] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const works = ['WAREHOUSE', 'EVENT'].includes(user.role) || user.eventWorkEnabled || user.warehouseWorkEnabled;
  useEffect(() => { if (works) api.getMySettlements().then(r => setHist(r.history || [])).catch(() => setHist([])); }, [works]);
  if (!works) return null;
  const eventy = user.role === 'EVENT' || user.eventWorkEnabled;
  return (
    <div className="card" id="wyplaty">
      <div className="flex" style={{ alignItems: 'baseline', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>💰 Moje wypłaty</h3>
        {eventy && <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowEvents(true)}>🎪 Moje eventy i zarobki</button>}
      </div>
      {hist === null ? <div style={{ fontSize: 11, color: '#94A3B8' }}>Ładowanie…</div>
        : hist.length === 0 ? <div style={{ fontSize: 11, color: '#94A3B8' }}>Jeszcze nie było wypłaty rozliczonej w aplikacji.</div>
        : hist.map(h => (
          <div key={h.id} style={{ borderTop: '1px solid #E2E8F0', padding: '6px 0', fontSize: 12 }}>
            <div className="flex gap-8" style={{ alignItems: 'baseline' }}>
              <b>{Number(h.amount).toFixed(2).replace('.', ',')} zł</b>
              <span style={{ color: '#64748B' }}>za okres do {new Date(h.upToDate).toLocaleDateString('pl-PL')}</span>
              <span style={{ color: '#94A3B8', fontSize: 10, marginLeft: 'auto' }}>rozliczono {new Date(h.createdAt).toLocaleDateString('pl-PL')}</span>
            </div>
            <SettlementSpec id={h.id} label="za co" />
          </div>
        ))}
      {showEvents && <PersonEventsModal userId={user.id} name={fullName(user)} onClose={() => setShowEvents(false)} />}
    </div>
  );
}

export function AccountPage() {
  const { user } = useAuth();
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [rep, setRep] = useState('');
  const [busy, setBusy] = useState(false);
  const { show, ToastComponent } = useToast();

  const tooShort = nw.length > 0 && nw.length < 10; // T172: min. 10, popularne hasła odrzuca serwer
  const mismatch = rep.length > 0 && nw !== rep;
  const sameAsOld = nw.length > 0 && nw === cur;
  const ready = cur.length > 0 && nw.length >= 10 && nw === rep && !sameAsOld;

  const submit = async () => {
    setBusy(true);
    try {
      await api.changePassword(cur, nw);
      show('Hasło zmienione. Przy następnym logowaniu użyj nowego.');
      setCur(''); setNw(''); setRep('');
    } catch (e) {
      show(e.message || 'Nie udało się zmienić hasła', 'error');
    }
    setBusy(false);
  };


  return (
    <div className="flex flex-col gap-14">
      <h2 className="page-title">Moje konto</h2>
      {(user?.role === 'EVENT' || user?.eventWorkEnabled) && <ContactDataCard />}
      {user && <MyRatesCard user={user} />}
      {user && <MyPayoutsCard user={user} />}

      <div className="card">
        <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>Twoje dane</h3>
        <div style={{ fontSize: 12, color: '#334155', lineHeight: 2 }}>
          <div><b>{user?.firstName} {user?.lastName}</b></div>
          <div>{user?.email}</div>
          <div style={{ color: '#64748B' }}>{ROLE_LABELS[user?.role] || user?.role}{user?.position ? ` · ${user.position}` : ''}</div>
          {user?.hasLeaveEntitlement !== false && (
            <div style={{ color: '#64748B' }}>
              Urlop: {fmtLeaveDays(leaveLeft(user))} z {user?.leaveDaysTotal || 0} dni
            </div>
          )}
        </div>
        <div style={{ marginTop: 10, padding: 8, background: '#F8FAFC', borderRadius: 6, fontSize: 11, color: '#64748B' }}>
          Dane osobowe i stawki zmienia administrator. Jeśli coś się nie zgadza — zgłoś to przełożonemu.
        </div>
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>Zmiana hasła</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 380 }}>
          <div className="input-group">
            <label>Obecne hasło</label>
            <input type="password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="input-group">
            <label>Nowe hasło (min. 10 znaków)</label>
            <input type="password" value={nw} onChange={e => setNw(e.target.value)} autoComplete="new-password" />
            {tooShort && <span style={{ fontSize: 10, color: '#DC2626' }}>Hasło musi mieć minimum 10 znaków.</span>}
            {sameAsOld && <span style={{ fontSize: 10, color: '#DC2626' }}>Nowe hasło musi różnić się od obecnego.</span>}
          </div>
          <div className="input-group">
            <label>Powtórz nowe hasło</label>
            <input type="password" value={rep} onChange={e => setRep(e.target.value)} autoComplete="new-password" />
            {mismatch && <span style={{ fontSize: 10, color: '#DC2626' }}>Hasła nie są takie same.</span>}
          </div>
          <button className="btn btn-primary" disabled={!ready || busy} onClick={submit} style={{ alignSelf: 'flex-start' }}>
            {busy ? 'Zapisywanie...' : 'Zmień hasło'}
          </button>
        </div>
      </div>

      <div className="card" style={{ background: '#F8FAFC', fontSize: 11, color: '#64748B' }}>
        <b>Nie pamiętasz hasła?</b> Skontaktuj się z administratorem — ustawi Ci nowe.
        Aplikacja nie wysyła linków resetujących.
      </div>

      {ToastComponent}
    </div>
  );
}

// ========== SALDA URLOPOWE (T26) — Biuro i Admin ==========
const BALANCE_SORT = {
  employee: b => `${b.user.lastName} ${b.user.firstName}`,
  total: b => b.total, used: b => b.used, planned: b => b.planned,
  remaining: b => b.remaining, pendingDays: b => b.pendingDays,
};
/** T97: ewidencja pracy zdalnej dla kadr — miesiąc, kto, ile dni, w jakich terminach; CSV. */
function RemoteWorkReport() {
  const [month, setMonth] = useState(() => new Date().toLocaleDateString('sv-SE').slice(0, 7));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { setErr(''); api.getRemoteReport(month).then(setData).catch(e => setErr(e.message)); }, [month]);
  const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString('pl-PL');
  const range = (p) => p.from === p.to ? fmt(p.from) : `${fmt(p.from)} – ${fmt(p.to)}`;
  const exportCsv = () => {
    const out = [['Pracownik', 'Rola', 'Stanowisko', 'Dni pracy zdalnej w miesiącu', 'Terminy', 'W roku (dni)', 'Limit roczny', 'Zatwierdził']];
    (data?.rows || []).forEach(r => out.push([fullName(r.user), ROLE_LABELS[r.user.role] || r.user.role, r.user.position || '', r.days,
      r.periods.map(p => `${range(p)} (${p.days})`).join(', '), r.yearUsed, r.yearLimit || 'bez limitu',
      [...new Set(r.periods.map(p => p.approvedBy ? fullName(p.approvedBy) : ''))].filter(Boolean).join(', ')]));
    const csv = '\uFEFF' + out.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `praca-zdalna-${month}.csv`; a.click();
  };
  return (
    <div className="card">
      <div className="flex gap-8 flex-wrap" style={{ alignItems: 'flex-end', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>💻 Praca zdalna — ewidencja</h3>
        <div className="input-group" style={{ marginLeft: 'auto' }}><label>Miesiąc</label><input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} /></div>
        <button className="btn btn-outline btn-sm" onClick={exportCsv} disabled={!data?.rows?.length}>Eksport CSV</button>
      </div>
      {err && <div style={{ color: '#DC2626', fontSize: 12 }}>{err}</div>}
      {data && (
        <>
          <div style={{ fontSize: 11, color: '#64748B', marginBottom: 8 }}>
            Zatwierdzone wnioski o pracę zdalną; liczone dni robocze w wybranym miesiącu ({data.workdaysInMonth} dni roboczych).
            Razem: <b>{data.totalDays}</b> dni, <b>{data.rows.length}</b> os.
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Pracownik</th><th>Rola / stanowisko</th><th>Dni w miesiącu</th><th>Terminy</th><th>W roku</th><th>Zatwierdził</th></tr></thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.user.id}>
                    <td><PersonCell name={fullName(r.user)} /></td>
                    <td><RoleBadge role={r.user.role} />{r.user.position && <div style={{ fontSize: 10, color: '#64748B' }}>{r.user.position}</div>}</td>
                    <td><b>{r.days}</b></td>
                    <td style={{ fontSize: 11 }}>{r.periods.map(p => <div key={p.id}>{range(p)} <span style={{ color: '#94A3B8' }}>({p.days} d.)</span></div>)}</td>
                    <td style={{ fontSize: 11, color: r.yearLimit && r.yearUsed >= r.yearLimit ? '#DC2626' : '#334155' }}>{r.yearUsed}{r.yearLimit ? ` / ${r.yearLimit}` : ''}</td>
                    <td style={{ fontSize: 11, color: '#64748B' }}>{[...new Set(r.periods.map(p => p.approvedBy ? fullName(p.approvedBy) : '—'))].join(', ')}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8', padding: 16 }}>W tym miesiącu nikt nie pracował zdalnie</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function LeaveBalancesPage() {
  const { canAccounting } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const rows = (data?.employees || []).filter(b => !q || `${b.user.firstName} ${b.user.lastName}`.toLowerCase().includes(q.toLowerCase()));
  const bs = useSort(rows, BALANCE_SORT, 'employee', 'asc');
  useEffect(() => { api.getLeaveBalances().then(setData).catch(console.error); }, []);

  const exportCsv = () => {
    const out = [['Pracownik', 'Rola', 'Przysługuje', 'Wykorzystane', 'Zaplanowane', 'Pozostało', 'Oczekuje (dni)']];
    bs.sorted.forEach(b => out.push([fullName(b.user), ROLE_LABELS[b.user.role] || b.user.role, b.total, fmtLeaveDays(b.used), fmtLeaveDays(b.planned), fmtLeaveDays(b.remaining), fmtLeaveDays(b.pendingDays)]));
    const csv = '\uFEFF' + out.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `salda-urlopowe-${data?.year || ''}.csv`; a.click();
  };

  if (!data) return <div style={{ color: '#94A3B8', padding: 20 }}>Ładowanie...</div>;
  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Salda urlopowe {data.year}</h2>
        <button className="btn btn-outline btn-sm" onClick={exportCsv}>Eksport CSV</button>
      </div>
      <div className="flex gap-8 flex-wrap">
        <div className="card stat-card"><div className="stat-label">Pracowników z urlopem</div><div className="stat-value">{data.employees.length}</div></div>
        <div className="card stat-card"><div className="stat-label">Wykorzystane</div><div className="stat-value" style={{ color: '#2563EB' }}>{fmtLeaveDays(data.totals.used)}</div><div className="stat-sub">dni, odbyte</div></div>
        <div className="card stat-card"><div className="stat-label">Zaplanowane</div><div className="stat-value" style={{ color: '#D97706' }}>{fmtLeaveDays(data.totals.planned)}</div><div className="stat-sub">zatwierdzone, przyszłe</div></div>
        <div className="card stat-card"><div className="stat-label">Pozostało</div><div className="stat-value" style={{ color: '#059669' }}>{fmtLeaveDays(data.totals.remaining)}</div><div className="stat-sub">z {data.totals.total} przysługujących</div></div>
        <div className="card stat-card"><div className="stat-label">Oczekuje</div><div className="stat-value" style={{ color: data.totals.pendingDays ? '#D97706' : '#94A3B8' }}>{fmtLeaveDays(data.totals.pendingDays)}</div><div className="stat-sub">dni we wnioskach</div></div>
      </div>
      <div className="card">
        <div className="input-group" style={{ maxWidth: 260, marginBottom: 10 }}><label>Szukaj</label><input value={q} onChange={e => setQ(e.target.value)} placeholder="nazwisko lub imię" /></div>
        <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>Liczą się tylko urlop wypoczynkowy i na żądanie. Okolicznościowy, opieka, ojcowski, macierzyński, rodzicielski, L4 i bezpłatny nie zmniejszają puli.</div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr>
              <SortTh label="Pracownik" k="employee" sort={bs.sort} onSort={bs.toggle} />
              <th>Rola</th>
              <SortTh label="Przysługuje" k="total" sort={bs.sort} onSort={bs.toggle} />
              <SortTh label="Wykorzystane" k="used" sort={bs.sort} onSort={bs.toggle} />
              <SortTh label="Zaplanowane" k="planned" sort={bs.sort} onSort={bs.toggle} />
              <SortTh label="Pozostało" k="remaining" sort={bs.sort} onSort={bs.toggle} />
              <SortTh label="Oczekuje" k="pendingDays" sort={bs.sort} onSort={bs.toggle} />
            </tr></thead>
            <tbody>
              {bs.sorted.map(b => (
                <tr key={b.user.id}>
                  <td><PersonCell name={fullName(b.user)} /></td>
                  <td><RoleBadge role={b.user.role} /></td>
                  <td>{b.total}</td>
                  <td style={{ color: '#2563EB' }}>{fmtLeaveDays(b.used)}</td>
                  <td style={{ color: '#D97706' }}>{b.planned ? fmtLeaveDays(b.planned) : '—'}</td>
                  <td><b style={{ color: b.remaining <= 0 ? '#DC2626' : '#059669' }}>{fmtLeaveDays(b.remaining)}</b></td>
                  <td style={{ color: '#D97706' }}>{b.pendingDays ? fmtLeaveDays(b.pendingDays) : "—"}</td>
                </tr>
              ))}
              {bs.sorted.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak pracowników</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {canAccounting && <RemoteWorkReport />}
    </div>
  );
}
